import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useRef
import { useLocation } from 'react-router-dom';
import { Viewer, Entity, PointGraphics, GeoJsonDataSource, Cesium3DTileset } from "resium"; // Changed ModelGraphics to Cesium3DTileset
import * as Cesium from "cesium"; // Import all of Cesium as a namespace
import { mapConfig } from '../../config/mapConfig'; // Import config
import tokenService from '../../services/auth/tokenService';
// Add authentication imports
import { useAuth } from '../../hooks/auth/useAuth'; // Import authentication hook
import { default as api, assetsApi } from '../../services/api'; // Import authenticated API instance
import {
  Ion, // Still needed for setting token
  Cartesian3,
  BingMapsImageryProvider,
  OpenStreetMapImageryProvider,
  ImageryLayer,
  UrlTemplateImageryProvider,
  BingMapsStyle, // Added import
  WebMercatorTilingScheme, // Added import
  Color, // Added Color
  // ScreenSpaceEventHandler, // Moved to InteractionHandler
  // ScreenSpaceEventType, // Moved to InteractionHandler
  defined, // Added for checking picked objects
  // Added for GeoJSON styling - Moved to LayerControl
  // ColorMaterialProperty,
  // HeadingPitchRoll, // Example variable removed
  // Transforms, // Example variable removed
  Cesium3DTileStyle, // Added back for 3D tile styling
  // Added for CZML animation
  CzmlDataSource,
  SampledPositionProperty,
  JulianDate,
  ClockRange,
  VelocityOrientationProperty,
  ShadowMode,
  TimeIntervalCollection,
  TimeInterval,
} from "cesium";
import { Box, Tooltip, IconButton, Zoom, Badge, Snackbar, Alert as MuiAlert, Fab, Dialog, DialogContent, Paper, Typography, DialogTitle, Grid, Button, Slide, Tabs, Tab, Fade, Divider } from '@mui/material'; // Added Slide, Tabs, Tab, Fade, Divider
import BasemapPicker from './controls/BasemapPicker'; // Updated path
import TerrainPicker from './controls/TerrainPicker'; // Import the new TerrainPicker component
import AssetDetailDrawer from './details/AssetDetailDrawer'; // Updated path
import IoTDetailDrawer from './details/IoTDetailDrawer'; // Updated path
// import FilterPanel from './controls/FilterPanel'; // Rendered by FilterManager
import FilterManager from './controls/FilterManager'; // Updated path
import OperationFilterManager from './controls/OperationFilterManager'; // Import the new operation filter manager
import TimelineSlider from './controls/TimelineSlider'; // Import the new timeline slider
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import EntityPointLayer from './layers/EntityPointLayer'; // Updated path
// import Flood_IoT_sensor from './layers/Flood_IoT_sensor'; // Rendered by LayerControl
import LayerControl from './layers/LayerControl'; // Updated path
import AssetFootprintLayer from './layers/AssetFootprintLayer'; // Import the new footprint layer
import InteractionHandler from './interactions/InteractionHandler'; // Updated path
import AnimationManager from './animation/AnimationManager'; // Import the new component
// Remove DroneInspectionLayer import
import { useCesiumViewer } from '../../context/CesiumViewerContext'; // Import the context hook
import DetectionDetailModal from '../Dashboard/DetectionDetailModal'; // Import the detection modal
import { envisFindingsApi, organizationLayersApi } from '../../services/api'; // Import findings API for Operation Mode and organization layers API
import InspectionPanel from './details/InspectionPanel'; // Import the new inspection panel
import AddInspectionModal from './modals/AddInspectionModal'; // Import the new AddInspectionModal
import EditInspectionModal from './modals/EditInspectionModal'; // Import the EditInspectionModal
import InspectionDetailsModal from './modals/InspectionDetailsModal';
import Enhanced_ImageViewerModal from './modals/Enhanced_ImageViewerModal'; // Update the import for the renamed photo viewer modal
import BatchUploadModal from './modals/BatchUploadModal'; // Import the new BatchUploadModal component
// Import DefectValidationModal for Operation Mode defect viewing
import DefectValidationModal from '../Defects(ENVIS)/modals/DefectValidationModal';
import { classificationApi } from '../../services/api'; // Import classification API for defect types
// OperationFilterPanel is now handled by OperationFilterManager

// Import terrain service functions
import { 
  createTerrainProvider, 
  optimizeTerrainPerformance, 
  cleanupTerrainProvider 
} from '../../services/terrainService'; // Import terrain service functions

// Import icons for basemap picker
import PublicIcon from '@mui/icons-material/Public';
import SatelliteIcon from '@mui/icons-material/Satellite';
import MapIcon from '@mui/icons-material/Map'; // Example for another layer
// import FilterListIcon from '@mui/icons-material/FilterList'; // Used within FilterManager
import TrackChangesIcon from '@mui/icons-material/TrackChanges'; // Import for tracking button
import CloseIcon from '@mui/icons-material/Close'; // Import for close button
import LocationOnIcon from '@mui/icons-material/LocationOn'; // Import for location icon
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // Import for batch upload button
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'; // Import for drawer handle
import SpeedIcon from '@mui/icons-material/Speed';
import LayersIcon from '@mui/icons-material/Layers';
import RouteIcon from '@mui/icons-material/Route';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Replace with Info icon
import HomeIcon from '@mui/icons-material/Home'; // Import for home button
import FullscreenIcon from '@mui/icons-material/Fullscreen'; // Import for fullscreen inspection mode
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'; // Import for exit fullscreen

// Set Cesium Ion default access token from config
Ion.defaultAccessToken = mapConfig.cesiumIonToken;

// Define available basemaps here, including provider factories
const basemapOptions = [
  {
    id: 'bing-aerial-labels',
    name: 'Bing Maps Aerial with Labels',
    icon: <SatelliteIcon fontSize="small" />,
    providerFactory: async () => await BingMapsImageryProvider.fromUrl('//dev.virtualearth.net', {
        key: Ion.defaultAccessToken, // Use token for key if needed, or specific Bing key
        mapStyle: BingMapsStyle.AERIAL_WITH_LABELS // Requires importing BingMapsStyle
    })
  },
   {
    id: 'bing-aerial',
    name: 'Bing Maps Aerial',
    icon: <SatelliteIcon fontSize="small" />,
    providerFactory: async () => await BingMapsImageryProvider.fromUrl('//dev.virtualearth.net', {
        key: Ion.defaultAccessToken, // Use token for key if needed, or specific Bing key
        mapStyle: BingMapsStyle.AERIAL // Requires importing BingMapsStyle
    })
  },
  {
    id: 'osm',
    name: 'OpenStreetMap',
    icon: <PublicIcon fontSize="small" />,
    providerFactory: () => new OpenStreetMapImageryProvider({
        url : 'https://a.tile.openstreetmap.org/'
    })
  },
  {
    id: 'esri-world-imagery',
    name: 'ESRI World Imagery',
    icon: <MapIcon fontSize="small" />, // Using MapIcon as example
    providerFactory: () => new UrlTemplateImageryProvider({
        url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        credit: 'Tiles © Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        tilingScheme: new WebMercatorTilingScheme(), // Corrected usage
        maximumLevel: 19
    })
  },
  {
    id: 'stadia-alidade-smooth',
    name: 'Stadia Alidade Smooth',
    icon: <MapIcon fontSize="small" />, // Using MapIcon, change if desired
    providerFactory: () => new UrlTemplateImageryProvider({
        url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png?api_key={YOUR_STADIA_API_KEY}', // Replace with your API key if needed
        credit: '© Stadia Maps, © OpenMapTiles © OpenStreetMap contributors',
        maximumLevel: 20 // Verify max level if needed
    })
  },
  {
    id: 'stadia-alidade-smooth-dark',
    name: 'Stadia Alidade Smooth Dark',
    icon: <MapIcon fontSize="small" sx={{ color: '#555' }} />, // Darker icon to indicate dark theme
    providerFactory: () => new UrlTemplateImageryProvider({
        url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}.png?api_key={YOUR_STADIA_API_KEY}', // Replace with your API key if needed
        credit: '© Stadia Maps, © OpenMapTiles © OpenStreetMap contributors',
        maximumLevel: 20
    })
  }
  // Add more options if needed
];

// Define the category colors (matching those in EntityPointLayer.js)
const categoryColors = {
  'Road Pavement': '#e74c3c',    // Red
  'Structural': '#3498db',       // Blue
  'Geotechnical': '#9b59b6',     // Purple
  'Road Furniture': '#16a085',   // Teal
  'Vertical Asset': '#7c4dff',   // Deep Purple
  'default': '#95a5a6'           // Grey (default)
};



// Toggle for the bottom-left status overlay (user/org/alt/terrain)
// Keep implementation but allow it to be hidden without deletion
const SHOW_STATUS_OVERLAY = false;

const MapViewPage = () => {
  const location = useLocation();
  // Get viewerRef from context instead of creating it locally
  const viewerRef = useCesiumViewer();
  
  // Add authentication context
  const { user, isAuthenticated, isOrganizationLevel, getUserOrganization, canEdit, hasDiscipline } = useAuth();
  const userRole = user?.role;
  const isSystemAdmin = userRole === 'admin' && user?.organizationId === null;

  // Check if user has any disciplines assigned (system admins have all disciplines)
  const hasAnyDiscipline = isSystemAdmin || (user?.permissions?.disciplines?.length > 0);

  // Check if user has Road Pavement discipline (required for Operation tab - ENViS findings)
  const hasRoadPavementDiscipline = isSystemAdmin || hasDiscipline('Road Pavement');

  const userOrganization = getUserOrganization();
  
  // Replace hardcoded organization ID with user's organization
  const PLUS_MALAYSIA_ORGANIZATION_ID = userOrganization?.id || null;
  
  const [assets, setAssets] = useState([]); // State for assets
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [selectedAsset, setSelectedAsset] = useState(null); // State for selected asset
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // State for drawer visibility
  // Layer visibility state remains here as it affects MapViewPage rendering directly
  const [showRoadLines, setShowRoadLines] = useState(true); // Show road lines by default: TRUE
  const [showIoTSensors, setShowIoTSensors] = useState(false); // Show IoT sensors by default: FALSE
  const [showAssetFootprints, setShowAssetFootprints] = useState(false); // Show asset geometry footprints by default: FALSE

  // Organization-specific GeoJSON layers state
  const [organizationLayers, setOrganizationLayers] = useState([]);
  const [loadingOrganizationLayers, setLoadingOrganizationLayers] = useState(false);

  // State to hold the combined filter values received from FilterManager
  const [currentFilters, setCurrentFilters] = useState({
    organization_id: '',
    network_ids: [], // Changed to array for multi-select
    section_ids: [], // Changed to array for multi-select
    regions: [], // Changed to array for multi-select
    category_id: '',
    type_id: '',
    subtype_id: '',
    searchTerm: '',
    status: '',
    rating_level_id: '', // Added rating filter
    slope_type: '', // Slope type filter (C=Cut, E=Embankment)
    is_critical: '' // Critical asset filter
  });

  // Add state for IoT sensor detail drawer
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [isIoTDrawerOpen, setIsIoTDrawerOpen] = useState(false);

  // Add state for 3D model visibility - but don't load on initialization
  const [show3DModel, setShow3DModel] = useState(false);
  const [selectedTilesetAsset, setSelectedTilesetAsset] = useState(null);
  const [available3dModelVersions, setAvailable3dModelVersions] = useState({ latest: null, available: [] });
  const [selected3dModelVersion, setSelected3dModelVersion] = useState(null);
  const isInitialModelLoad = useRef(true); // Use a ref to track initial model load without causing re-renders
  
  // Tileset state moved to LayerControl
  // CZML DataSource and Animation Truck state moved to AnimationManager
  const [trackedTruckEntityRef, setTrackedTruckEntityRef] = useState(null); // State to hold truck ref for tracking toggle
  
  // Add state for pothole detection modal
  const [selectedPothole, setSelectedPothole] = useState(null);
  const [isPotholeModalOpen, setIsPotholeModalOpen] = useState(false);
  const [isInitialViewSettled, setIsInitialViewSettled] = useState(false); // State to track if initial view is set
  const [cameraHeight, setCameraHeight] = useState(null); // State for camera height display
  const [shouldLoadAnimations, setShouldLoadAnimations] = useState(false); // State to trigger truck animations
  // State for Snackbar notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info'); // 'info', 'warning', 'error', 'success'
  
  // Example model position - Removed as unused
  // const modelPosition = Cartesian3.fromDegrees(101.9758, 4.2105, 0);
  
  // Example model orientation - Removed as unused
  // const modelOrientation = Transforms.headingPitchRollQuaternion(
  //   modelPosition,
  //   new HeadingPitchRoll(0, 0, 0)
  // );

  // Define initial camera position from config
  const initialCameraPosition = Cartesian3.fromDegrees(
    mapConfig.initialCamera.longitude,
    mapConfig.initialCamera.latitude,
    mapConfig.initialCamera.height
  );

  // Define home camera position (exact Cartesian3)
  const homeCameraPosition = new Cartesian3(
    -2956398.4816439617,
    8348611.767285023,
    616336.2896083156
  );

  // Add state for tracking mode
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false);

  // Add state for inspection panel and gallery
  const [showInspectionPanel, setShowInspectionPanel] = useState(false);
  const [filterCloseSignal, setFilterCloseSignal] = useState(0); // Signal to close filter panels when inspection opens
  const [inspectionAsset, setInspectionAsset] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showInspectionGallery, setShowInspectionGallery] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false); // State for photo viewer modal
  const [selectedPhoto, setSelectedPhoto] = useState(null); // State for the selected photo object
  const [isPhotoViewOnly, setIsPhotoViewOnly] = useState(false); // New state for viewOnlyMode
  // New states for photo navigation
  const [photoArray, setPhotoArray] = useState([]); // Array of all photos for navigation
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0); // Current photo index for navigation
  // Add state for add inspection modal
  const [showAddInspectionModal, setShowAddInspectionModal] = useState(false);
  const [showEditInspectionModal, setShowEditInspectionModal] = useState(false);
  const [editInspection, setEditInspection] = useState(null);
  // Add refresh key to trigger inspection panel refresh
  const [inspectionRefreshKey, setInspectionRefreshKey] = useState(0);

  // Full-screen inspection mode state
  const [inspectionFullScreenMode, setInspectionFullScreenMode] = useState(false);
  const [fullscreenSidebarWidth, setFullscreenSidebarWidth] = useState(360);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);

  // Add these state variables to the existing state declarations
  const [selectedFootprintAsset, setSelectedFootprintAsset] = useState(null); // State for asset that has footprint visible
  const [isFootprintVisible, setIsFootprintVisible] = useState(false); // State for footprint visibility

  // Add state for batch upload modal
  const [batchUploadModalOpen, setBatchUploadModalOpen] = useState(false);
  
  // Add state for quick access drawer
  const [quickAccessOpen, setQuickAccessOpen] = useState(false);

  // Add state for legend visibility
  const [legendOpen, setLegendOpen] = useState(false);

  // NEW: Terrain-related state
  const [currentTerrainProvider, setCurrentTerrainProvider] = useState(null);
  const [terrainEnabled, setTerrainEnabled] = useState(false);
  const [terrainLoading, setTerrainLoading] = useState(false);

  // Add state for tab selection - default to Asset (0) if user doesn't have Road Pavement discipline
  const [selectedTab, setSelectedTab] = useState(hasRoadPavementDiscipline ? 1 : 0); // 0 for Asset Mode, 1 for Operation Mode
  
  // Add state for Operation Mode defects/findings
  const [operationDefects, setOperationDefects] = useState([]); // State for defects/findings raw list
  const [filteredOperationDefects, setFilteredOperationDefects] = useState([]); // State for defects/findings after filters
  const [loadingOperationDefects, setLoadingOperationDefects] = useState(false);

  // Add state for DefectValidationModal in Operation Mode
  const [defectValidationModalOpen, setDefectValidationModalOpen] = useState(false);
  const [selectedDefectForValidation, setSelectedDefectForValidation] = useState(null);
  const [defectTypesList, setDefectTypesList] = useState([]);
  const [loadingDefectTypes, setLoadingDefectTypes] = useState(false);

  // Add state for OperationFilterPanel
  const [operationFilters, setOperationFilters] = useState({
    defectTypeId: '',
    deviceId: '',
    networkId: '',
    sectionId: '',
    region: '',
    validationStatus: '',
    actionStatus: '',
    dateRange: 'today',
    customDateFrom: '',
    customDateTo: '',
    searchTerm: ''
  });
  const [operationFilterOptions, setOperationFilterOptions] = useState({
    defectTypes: [],
    devices: [],
    networks: [],
    sections: [],
    regions: []
  });
  const [showOperationFilterPanel, setShowOperationFilterPanel] = useState(false);
  
  // State for operation filter manager
  const [operationFilterManagerFilters, setOperationFilterManagerFilters] = useState({});

  // Handler for tab change
  const handleTabChange = (event, newValue) => {
    console.log(`MapViewPage: Switching from tab ${selectedTab} to tab ${newValue}`);
    setSelectedTab(newValue);
    
    // If switching to Operation mode (tab 1), clear all loaded assets/markers
    if (newValue === 1) {
      console.log('MapViewPage: Switching to Operation mode - clearing all assets and markers');
      setAssets([]); // Clear assets array to unload markers
      
      // Close any open drawers/panels
      setIsDrawerOpen(false);
      setSelectedAsset(null);
      setIsIoTDrawerOpen(false);
      setSelectedSensor(null);
      
      // Close inspection panel if open
      if (showInspectionPanel) {
        setShowInspectionPanel(false);
        setInspectionAsset(null);
      }
      
      // Close 3D model if visible
      if (show3DModel) {
        setShow3DModel(false);
        setSelectedTilesetAsset(null);
      }
      
      // Hide footprint if visible
      if (isFootprintVisible) {
        setIsFootprintVisible(false);
        setSelectedFootprintAsset(null);
      }
      
      // Clear any plotted inspection photos from the map
      const viewer = viewerRef.current?.cesiumElement;
      if (viewer) {
        try {
          const entitiesToRemove = viewer.entities.values.filter(
            entity => entity.id.startsWith('inspection-photo-')
          );
          entitiesToRemove.forEach(entity => {
            viewer.entities.remove(entity);
          });
          if (entitiesToRemove.length > 0) {
            console.log(`MapViewPage: Removed ${entitiesToRemove.length} inspection photo entities from map`);
          }
        } catch (error) {
          console.error('Error removing inspection photos during tab switch:', error);
        }
      }
      
      // Show notification (will be updated when defects are loaded)
      setSnackbarMessage('Switched to Operation mode - loading today\'s defects...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } else if (newValue === 0) {
      // Switching back to Asset mode
      console.log('MapViewPage: Switching to Asset mode - markers will reload based on current filters');
      setSnackbarMessage('Switched to Asset mode');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    }
  };

  // Helper function to determine if a button should be visible based on mode
  const isButtonVisible = (buttonType) => {
    // Check authentication first
    if (!isAuthenticated) return false;
    
    if (selectedTab === 0) { // Asset Mode
      // Role-based feature access for Asset Mode
      switch (buttonType) {
        case 'filter':
          return true; // All authenticated users can filter
        case 'basemap':
        case 'home':
        case 'terrain':
        case 'legend':
          return true; // Basic map controls for all users
        case 'batchUpload':
          return false; // Temporarily hidden - TODO: Re-enable when needed
          // return canEdit; // Only users who can edit (admin, manager, inspector)
        default:
          return false;
      }
    } else { // Operation Mode
      // Role-based feature access for Operation Mode
      switch (buttonType) {
        case 'operationFilter':
          return true; // All authenticated users can filter in Operation Mode
        case 'basemap':
        case 'home':
        case 'terrain':
        case 'legend':
          return true; // Basic map controls for all users
        case 'tracking':
          return isSystemAdmin || userRole === 'manager' || userRole === 'inspector'; // Only admins, managers, and inspectors can use tracking
        default:
          return false;
      }
    }
  };

  // Function to toggle quick access drawer
  const toggleQuickAccess = () => {
    setQuickAccessOpen(prevState => !prevState);
  };

  // Function to toggle legend
  const toggleLegend = () => {
    setLegendOpen(prevState => !prevState);
  };

  const quickAccessRegions = [
    { name: 'Central', value: 'Central', icon: <LocationOnIcon sx={{ fontSize: '1.2rem' }} /> },
    { name: 'Southern', value: 'Southern', icon: <LocationOnIcon sx={{ fontSize: '1.2rem' }} /> },
    { name: 'Northern', value: 'Northern', icon: <LocationOnIcon sx={{ fontSize: '1.2rem' }} /> },
    { name: 'Western', value: 'Western', icon: <LocationOnIcon sx={{ fontSize: '1.2rem' }} /> },
    { name: 'Eastern', value: 'Eastern', icon: <LocationOnIcon sx={{ fontSize: '1.2rem' }} /> },
  ];

  // Handler for quick region selection
  const handleQuickRegionSelect = (regionValue) => {
    if (!PLUS_MALAYSIA_ORGANIZATION_ID) {
      console.warn("User organization is not available for quick region filter.");
      // Show a snackbar warning to the user
      setSnackbarMessage("Unable to filter by region: User organization not found.");
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const newFilters = {
      organization_id: PLUS_MALAYSIA_ORGANIZATION_ID,
      network_ids: [],      // Reset dependent location filters (array)
      section_ids: [],      // Reset dependent location filters (array)
      regions: [regionValue], // Single region as array
      category_id: '',      // Reset classification filters
      type_id: '',          // Reset classification filters
      subtype_id: '',       // Reset classification filters
      status: '',           // Reset status filter
      rating_level_id: '',  // Reset rating filter
      searchTerm: '',       // Clear search term
    };
    
    // Apply the filters by calling the existing handler from FilterManager
    // This will update currentFilters in MapViewPage and trigger data fetching
    handleFilterChange(newFilters); 

    setQuickAccessOpen(false); // Close the drawer
  };

  // Handler function to change the base imagery layer
  const handleBasemapChange = async (providerFactory) => {
    const viewer = viewerRef.current?.cesiumElement; // Access the Cesium Viewer instance
    if (!viewer || typeof providerFactory !== 'function') return;

    try {
        const imageryLayers = viewer.imageryLayers;
        // Remove the current base layer (index 0)
        if (imageryLayers.length > 0) {
            imageryLayers.remove(imageryLayers.get(0));
        }
        // Create the new provider
        const newProvider = await providerFactory(); // Await if factory is async
        // Add the new layer at the bottom (index 0)
        imageryLayers.add(new ImageryLayer(newProvider), 0);
    } catch (error) {
        console.error("Error changing basemap:", error);
        // Optionally, add user feedback here
    }
  };

  // --- Filter data fetching logic moved to FilterManager ---
  // Fetch assets based on filters received from FilterManager
  // Effect to fetch assets based on any filter condition
  useEffect(() => {
    // Skip fetching assets if in Operation mode (tab 1)
    if (selectedTab === 1) {
      console.log('MapViewPage: Skipping asset fetch - currently in Operation mode');
      return;
    }

    const {
      organization_id,
      network_ids,
      section_ids,
      regions,
      category_id,
      type_id,
      subtype_id,
      searchTerm,
      status,
      rating_level_id,
      slope_type,
      is_critical
    } = currentFilters;

    const isAnyFilterActive =
      organization_id ||
      (network_ids && network_ids.length > 0) ||
      (section_ids && section_ids.length > 0) ||
      (regions && regions.length > 0) ||
      category_id ||
      type_id ||
      subtype_id ||
      searchTerm ||
      status ||
      rating_level_id ||
      slope_type ||
      is_critical;

    const fetchFilteredAssets = async () => {
      // Build query parameters using any non-empty filter
      const params = new URLSearchParams({ limit: mapConfig.assetFetchLimit });
      let activeFiltersForLog = {};

      if (organization_id) {
        params.append('organization_id', organization_id);
        activeFiltersForLog.organization_id = organization_id;
      }

      // Multi-select: send as comma-separated values
      if (network_ids && network_ids.length > 0) {
        params.append('network_ids', network_ids.join(','));
        activeFiltersForLog.network_ids = network_ids;
      }

      // Multi-select: send as comma-separated values
      if (section_ids && section_ids.length > 0) {
        params.append('section_ids', section_ids.join(','));
        activeFiltersForLog.section_ids = section_ids;
      }

      // Multi-select: send as comma-separated values
      if (regions && regions.length > 0) {
        params.append('regions', regions.join(','));
        activeFiltersForLog.regions = regions;
      }

      if (category_id) {
        params.append('category_id', category_id);
        activeFiltersForLog.category_id = category_id;
      }

      if (type_id) {
        params.append('type_id', type_id);
        activeFiltersForLog.type_id = type_id;
      }

      if (subtype_id) {
        params.append('subtype_id', subtype_id);
        activeFiltersForLog.subtype_id = subtype_id;
      }

      if (searchTerm) {
        params.append('search', searchTerm);
        activeFiltersForLog.search = searchTerm;
      }

      // Add status parameter if it exists
      if (status) {
        params.append('status', status);
        activeFiltersForLog.status = status;
      }

      // Add rating filter
      if (rating_level_id) {
        params.append('rating_level_id', rating_level_id);
        activeFiltersForLog.rating_level_id = rating_level_id;
      }

      // Add slope type filter (C=Cut, E=Embankment)
      if (slope_type) {
        params.append('slope_type', slope_type);
        activeFiltersForLog.slope_type = slope_type;
      }

      // Add critical asset filter
      if (is_critical) {
        params.append('is_critical', is_critical);
        activeFiltersForLog.is_critical = is_critical;
      }

      const queryString = params.toString();

      // Close drawer and clear selection when filters change, but not if an asset was just navigated to
      if (!location.state?.selectedAsset) {
        setIsDrawerOpen(false);
        setSelectedAsset(null);
      }

      try {
        console.log("MapViewPage: Fetching assets with authenticated API. Query params:", queryString);
        console.log("MapViewPage: Active filters sent to API:", activeFiltersForLog);
        
        // Use authenticated API instead of direct fetch
        const response = await api.get(`/assets?${queryString}`);
        const data = response.data;
        
        console.log(`MapViewPage: Received ${data.length} assets from authenticated API. Filters applied:`, activeFiltersForLog);
        setAssets(data); // Update state with filtered assets
      } catch (error) {
        console.error("Error fetching assets with authenticated API:", error);
        
        // Show user-friendly error message
        setSnackbarMessage('Failed to load assets. Please check your connection and try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        
        setAssets([]); // Clear assets on error
      }
    };

    if (isAnyFilterActive) {
      fetchFilteredAssets();
    } else {
      setAssets([]); // Clear assets if no filters are active
      console.log("No active filters. Clearing assets.");
    }
    
    // Re-run this effect whenever any part of currentFilters changes or selectedTab changes
  }, [currentFilters, selectedTab]);

  // Set initial camera view when viewer is ready
  useEffect(() => {
    const assetFromState = location.state?.selectedAsset;
    const setInitialView = () => {
      if (viewerRef.current?.cesiumElement && !isInitialViewSettled) {
        const viewer = viewerRef.current.cesiumElement;
        if (assetFromState && assetFromState.center_lng && assetFromState.center_lat) {
          // If an asset is passed in state, fly to it
          const assetPosition = Cartesian3.fromDegrees(
            parseFloat(assetFromState.center_lng),
            parseFloat(assetFromState.center_lat),
            1500 // Fly to a height of 1500m above the asset
          );
          viewer.camera.flyTo({
            destination: assetPosition,
            duration: 2.0
          });
          setSelectedAsset(assetFromState);
          setIsDrawerOpen(true);
        } else {
          // Otherwise, set the default initial view
          viewer.camera.setView({
            destination: initialCameraPosition
          });
        }
        console.log("MapViewPage: Initial camera view set.");
        setIsInitialViewSettled(true);
        return true; // Successfully set
      }
      return false; // Not ready yet
    };

    // Try to set initial view immediately
    if (setInitialView()) {
      return; // Successfully set, no need for interval
    }

    // If not ready immediately, poll until viewer is available
    const checkInterval = setInterval(() => {
      if (setInitialView()) {
        clearInterval(checkInterval);
      }
    }, 100); // Check every 100ms

    // Cleanup interval on unmount or when settled
    return () => {
      clearInterval(checkInterval);
    };
  }, [location.state, isInitialViewSettled, viewerRef, initialCameraPosition]);

  // Effect to set the correct tab when navigated from the Assets page
  useEffect(() => {
    const sourcePage = location.state?.source;
    if (sourcePage === 'assetsPage') {
      setSelectedTab(0); // Switch to Asset Mode
      console.log("MapViewPage: Navigated from Assets page, switching to Asset Mode.");
    }
  }, [location.state?.source]);

  // Initialize terrain provider on component mount
  useEffect(() => {
    // Terrain disabled by default - start with ellipsoid (flat)
    if (viewerRef.current?.cesiumElement) {
      viewerRef.current.cesiumElement.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      setCurrentTerrainProvider('ellipsoid');
      console.log('MapViewPage: Terrain disabled by default - using flat ellipsoid');
    }
  }, [viewerRef.current, isInitialViewSettled]);

  // Configure clock to show lighting as if it's 5pm Kuala Lumpur time
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;

    // 5:00 PM Malaysia Time (MYT = UTC+8) = 9:00 AM UTC
    // Create a specific date for 5pm KL time
    const klDate = new Date();
    klDate.setUTCHours(9, 0, 0, 0); // 9 AM UTC = 5 PM KL time
    
    const julianTime = JulianDate.fromDate(klDate);
    
    // Set the clock to this specific time and stop animation
    viewer.clock.currentTime = julianTime.clone();
    viewer.clock.startTime = julianTime.clone();
    viewer.clock.stopTime = julianTime.clone();
    viewer.clock.shouldAnimate = false; // Keep the time fixed
    viewer.clock.multiplier = 0; // No time progression
    
    console.log('MapViewPage: Clock set to 5pm Kuala Lumpur time for lighting');
  }, [viewerRef.current, isInitialViewSettled]);

  // Effect to listen for camera height changes
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;

    const updateHeightDisplay = () => {
      const height = viewer.camera.positionCartographic?.height;
      if (height !== undefined) {
        setCameraHeight(height);
      }
    };

    // Listen for when the camera stops moving
    viewer.camera.moveEnd.addEventListener(updateHeightDisplay);

    // Set initial height
    updateHeightDisplay();

    // Cleanup listener on unmount
    return () => {
      if (!viewer.isDestroyed()) { // Check if viewer is still valid
        viewer.camera.moveEnd.removeEventListener(updateHeightDisplay);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerRef.current]); // Rerun if viewer instance changes

  // --- Interaction handling logic moved to InteractionHandler ---

  // --- Filter change handlers moved to FilterManager ---

  // Handler to close the drawer (remains here)
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    // Optionally clear selected asset when closing, or keep it for context
    // setSelectedAsset(null);
  };

  // Callback to refresh selected asset data (e.g., after display image update)
  const handleAssetUpdate = async () => {
    if (selectedAsset?.id) {
      try {
        const response = await assetsApi.getById(selectedAsset.id);
        setSelectedAsset(response.data);
      } catch (error) {
        console.error('Failed to refresh asset data:', error);
      }
    }
  };

  // Callback for FilterManager to update filters in MapViewPage
  const handleFilterChange = (newFilters) => {
    console.log('Received new filters:', newFilters);

    // Make sure all expected properties are set to avoid undefined errors
    const updatedFilters = {
      organization_id: newFilters.organization_id || '',
      network_ids: newFilters.network_ids || [], // Array for multi-select
      section_ids: newFilters.section_ids || [], // Array for multi-select
      regions: newFilters.regions || [], // Array for multi-select
      category_id: newFilters.category_id || '',
      type_id: newFilters.type_id || '',
      subtype_id: newFilters.subtype_id || '',
      searchTerm: newFilters.searchTerm || '',
      status: newFilters.status || '',
      rating_level_id: newFilters.rating_level_id || '', // Rating filter
      slope_type: newFilters.slope_type || '', // Slope type filter
      is_critical: newFilters.is_critical || '' // Critical asset filter
    };

    setCurrentFilters(updatedFilters);
  };

  // Handler to close the IoT drawer
  const handleCloseIoTDrawer = () => {
    setIsIoTDrawerOpen(false);
    setSelectedSensor(null);
  };
  
  // Handler for IoT sensor selection
  const handleSensorSelect = useCallback((sensorData) => {
    setSelectedSensor(sensorData);
    setIsIoTDrawerOpen(true);
  }, []);

  // Handler to close the pothole detection modal
  const handleClosePotholeModal = () => {
    setIsPotholeModalOpen(false);
    setSelectedPothole(null);
  };
  
  // Update sensor data when it changes while drawer is open
  const handleSensorUpdate = useCallback((sensorData) => {
    if (isIoTDrawerOpen && selectedSensor && selectedSensor.id === sensorData.id) {
      setSelectedSensor(sensorData);
    }
  }, [isIoTDrawerOpen, selectedSensor]);

  // Handler for waypoint notifications from AnimationManager
  const handleWaypointNotification = (waypointInfo) => {
    // Customize message based on waypointInfo if needed
    // Construct the desired message format
    const inspectorName = "N4 Patrol Vehicle (VGQ2016)"; // Hardcoded for now
    const roadName = "NSE Section N4/KM193.45"; // Hardcoded for now - NOTE: This might not match the actual road of the animation
    setSnackbarMessage(`Pothole #${waypointInfo.id} is detected by ${inspectorName} at ${roadName}!`);
    setSnackbarSeverity('warning'); // Or 'info', 'error' based on data
    setSnackbarOpen(true);
  };

  // Handler to close the snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Modified toggle function for tracking mode and animation loading
  const toggleTracking = () => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;

    const newTrackingState = !isTrackingEnabled;
    setIsTrackingEnabled(newTrackingState);

    if (newTrackingState) {
      // If enabling tracking, also ensure animations are set to load
      console.log("MapViewPage: Enabling tracking and loading animations.");
      setShouldLoadAnimations(true);
      // Actual tracking (viewer.trackedEntity) will be handled by the useEffect below
      // once trackedTruckEntityRef is available.
    } else {
      // If disabling tracking, disable animations and reset camera
      console.log("MapViewPage: Disabling tracking and unloading animations.");
      setShouldLoadAnimations(false);
      viewer.trackedEntity = undefined;
      setTrackedTruckEntityRef(null); // Clear the reference
      viewer.camera.setView({
        destination: initialCameraPosition
      });
    }
  };

  // New useEffect to handle camera tracking when isTrackingEnabled and trackedTruckEntityRef are ready
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (viewer && isTrackingEnabled && trackedTruckEntityRef) {
      console.log("MapViewPage: trackedTruckEntityRef available, starting tracking.", trackedTruckEntityRef);
      viewer.trackedEntity = trackedTruckEntityRef;
    } else if (viewer && !isTrackingEnabled && viewer.trackedEntity) {
      // This case handles if tracking was disabled but viewer.trackedEntity wasn't cleared by toggleTracking
      // (e.g. if trackedTruckEntityRef became null while tracking was active)
      console.log("MapViewPage: Clearing tracked entity as tracking is disabled or ref is null.");
      viewer.trackedEntity = undefined;
    }
  }, [isTrackingEnabled, trackedTruckEntityRef, viewerRef]);

  // Fetch organization-specific GeoJSON layers
  useEffect(() => {
    const fetchOrganizationLayers = async () => {
      if (!userOrganization?.id) {
        console.log('MapViewPage: No user organization found, skipping organization layers fetch');
        setOrganizationLayers([]);
        return;
      }

      try {
        setLoadingOrganizationLayers(true);
        console.log(`MapViewPage: Fetching GeoJSON layers for organization ${userOrganization.id}`);
        
        const response = await organizationLayersApi.getLayers(userOrganization.id);
        const layers = response.data || [];
        
        console.log(`MapViewPage: Fetched ${layers.length} organization GeoJSON layers`);
        setOrganizationLayers(layers);
        
        if (layers.length > 0) {
          console.log('Organization layers:', layers.map(layer => ({ id: layer.id, name: layer.layer_name })));
        }
      } catch (error) {
        console.error('Error fetching organization GeoJSON layers:', error);
        setOrganizationLayers([]);
        
        // Only show error if it's not a 404 (no layers found)
        if (error.response?.status !== 404) {
          setSnackbarMessage('Failed to load organization map layers');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
        }
      } finally {
        setLoadingOrganizationLayers(false);
      }
    };

    fetchOrganizationLayers();
  }, [userOrganization?.id]); // Re-fetch when user organization changes

  // Handler for showing/hiding the inspection panel
  const handleShowInspection = (asset) => {
    const assetId = asset?.id || asset?.assetId;
    const currentAssetId = inspectionAsset?.id || inspectionAsset?.assetId;
    
    console.log(`Inspection toggle clicked for asset: ${assetId}`);
    
    // Check if the inspection panel for THIS asset is currently shown
    if (showInspectionPanel && currentAssetId === assetId) {
      // If yes, close it (toggle off)
      console.log(`Toggling OFF inspection panel for asset: ${assetId}`);
      setShowInspectionPanel(false);
      setInspectionAsset(null);
      
      // Also remove any plotted images from the map
      const viewer = viewerRef.current?.cesiumElement;
      if (viewer) {
        try {
          // Remove all inspection photo entities
          const entitiesToRemove = viewer.entities.values.filter(
            entity => entity.id.startsWith('inspection-photo-')
          );
          
          if (entitiesToRemove.length > 0) {
            console.log(`Removing ${entitiesToRemove.length} plotted images from map`);
            entitiesToRemove.forEach(entity => {
              viewer.entities.remove(entity);
            });
            
            // Show notification
            setSnackbarMessage('Removed inspection photos from map');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            
            // Force a refresh of the inspection panel to update button states
            setInspectionRefreshKey(prevKey => prevKey + 1);
          }
        } catch (error) {
          console.error('Error removing inspection images:', error);
        }
      }
    } else {
      // If not, show it (toggle on)
      console.log(`Toggling ON inspection panel for asset: ${assetId}`);
      setInspectionAsset(asset);
      setShowInspectionPanel(true);
      // Close any open filter panels
      setFilterCloseSignal(prev => prev + 1);
    }
  };

  // Handler for selecting an inspection to view details and images
  const handleSelectInspection = useCallback((inspection) => {
    setSelectedInspection(inspection);
    setShowInspectionGallery(true);
  }, []);

  // Handler for closing the inspection gallery
  const handleCloseInspectionGallery = () => {
    setShowInspectionGallery(false);
    setSelectedInspection(null);
    // Defensive: clear any pending resume flag so a stray photo-viewer close
    // doesn't unexpectedly reopen the inspection modal after the user dismissed it.
    resumeInspectionAfterPhotoRef.current = false;
  };

  // Define the callback function to trigger inspection panel refresh
  const triggerInspectionPanelRefresh = () => {
    setInspectionRefreshKey(prevKey => prevKey + 1);
    console.log('MapViewPage: Triggered inspection panel refresh via callback.');
  };

  // When the user clicks a photo inside InspectionDetailsModal we close that modal and
  // open Enhanced_ImageViewerModal. This ref records that fact so handleClosePhotoModal
  // can return the user to InspectionDetailsModal when they dismiss the photo viewer.
  // A ref (not state) is used so existing useCallback deps stay stable and there's no
  // closure-staleness risk inside handleClosePhotoModal.
  const resumeInspectionAfterPhotoRef = useRef(false);

  // Handler for when a photo is selected in the InspectionDetailsModal
  const handlePhotoSelect = (photo, photoArray = [], photoIndex = 0) => {
    console.log("Photo selected in MapViewPage:", { photo, photoArrayLength: photoArray.length, photoIndex }); // Debug log

    // Mark that the photo viewer was launched from inside the inspection modal so we
    // know to reopen the inspection modal when the photo viewer is dismissed.
    resumeInspectionAfterPhotoRef.current = true;

    // Use the new navigation-aware photo selection
    handlePhotoSelectWithNavigation(photo, photoArray, photoIndex);

    setShowInspectionGallery(false); // Close the details modal (will be reopened on photo viewer close)
  };



  // Handler for adding a new inspection
  const handleAddInspection = useCallback((asset) => {
    // Check if user can edit (create inspections)
    if (!canEdit) {
      setSnackbarMessage('You do not have permission to create inspections.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    setInspectionAsset(asset);
    setShowAddInspectionModal(true);
  }, [canEdit]);

  // Handler for closing the add inspection modal
  const handleCloseAddInspectionModal = () => {
    setShowAddInspectionModal(false);
  };

  // Handler for successful inspection creation
  const handleInspectionCreated = (newInspection) => {
    // Show success notification
    setSnackbarMessage(`Inspection created successfully for ${inspectionAsset?.name || 'asset'}`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    
    // Refresh inspections in the panel if open
    if (showInspectionPanel && inspectionAsset) {
      // Increment the refresh key to trigger a refresh in the InspectionPanel
      setInspectionRefreshKey(prevKey => prevKey + 1);
      console.log('Triggered inspection panel refresh with key:', inspectionRefreshKey + 1);
    }
  };

  // Handler for editing an inspection
  const handleEditInspection = useCallback((inspection) => {
    setEditInspection(inspection);
    setShowEditInspectionModal(true);
  }, []);

  const handleCloseEditInspectionModal = () => {
    setShowEditInspectionModal(false);
    setEditInspection(null);
  };

  const handleInspectionUpdated = () => {
    setSnackbarMessage('Inspection updated successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    setInspectionRefreshKey(prevKey => prevKey + 1);
  };

  // Handler for the 3D model button in asset drawer
  // Updated handler for the 3D model button to toggle visibility
  const handleShow3DModel = (asset) => {
    // This function now primarily just sets the asset and shows the model.
    // The version is derived from the state set by onVersionsLoaded.
   const latestVersion = available3dModelVersions.latest;

   if (!latestVersion) {
     setSnackbarMessage('No 3D model version found for this asset.');
     setSnackbarSeverity('warning');
     setSnackbarOpen(true);
     return;
   }

    const assetId = asset?.id || asset?.assetId;
    const currentAssetId = selectedTilesetAsset?.id || selectedTilesetAsset?.assetId;

    console.log(`3D Model toggle clicked for asset: ${assetId}`);

    // Check if the model for THIS asset is currently shown
    if (show3DModel && currentAssetId === assetId) {
      // If yes, close it (toggle off)
      console.log(`Toggling OFF 3D model for asset: ${assetId}`);
      handleClose3DModel(); // Use the existing close function
    } else {
      // If not, show it (toggle on)
      console.log(`Toggling ON 3D model for asset: ${assetId}`);
      // First close the drawer to prevent interference
      setIsDrawerOpen(false);
      
      // Set the selected asset and enable 3D model view
      setSelectedTilesetAsset(asset);
      setSelected3dModelVersion(latestVersion); // Set the selected version to the latest
      isInitialModelLoad.current = true; // Flag this as the initial load to trigger auto-zoom
      setShow3DModel(true);
      
      // Show notification
      setSnackbarMessage(`Loading 3D model for ${asset.name || 'asset'}`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      
      // Get asset coordinates for camera positioning
      if (asset.center_lat && asset.center_lng) {
        const viewer = viewerRef.current?.cesiumElement;
        if (viewer) {
          // Calculate a position with some height above the asset
          const modelPosition = Cartesian3.fromDegrees(
            parseFloat(asset.center_lng),
            parseFloat(asset.center_lat),
            // Set a higher initial height for better overview before model loads
            parseFloat(asset.center_elevation || 0) + 200
          );
          
          // Fly the camera to the model position with a tilt angle
          // Use a smoother approach that prepares for the 3D model
          viewer.camera.flyTo({
            destination: modelPosition,
            orientation: {
              heading: Cesium.Math.toRadians(45), // Angle to match final positioning
              pitch: Cesium.Math.toRadians(-45), // Look down at 45 degrees for initial view
              roll: 0
            },
            duration: 1.5 // Slightly faster initial animation
          });
        }
      }
    }
  };

  // Prefetch adjacent 3D tileset resources to speed up timeline switching
  const prefetchTilesetResources = useCallback(async (asset, version) => {
    try {
      if (!asset || !version) return;
      const token = tokenService.getAccessToken?.() || null;
      const orgId = asset.organization_id || asset.organizationId;
      const assetId = asset.id || asset.assetId;
      const base = `${process.env.REACT_APP_API_URL}/3d-model-proxy/${orgId}/${assetId}/${version}`;
      const tilesetUrl = `${base}/tileset.json`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const tilesetRes = await fetch(tilesetUrl, { headers });
      if (!tilesetRes.ok) return;
      const tileset = await tilesetRes.json();

      const urls = [];
      const root = tileset?.root;
      const toAbs = (u) => (typeof u === 'string' && (u.startsWith('http://') || u.startsWith('https://'))) ? u : `${base}/${u}`;
      if (root?.content?.uri) urls.push(toAbs(root.content.uri));
      if (Array.isArray(root?.children)) {
        for (let i = 0; i < Math.min(root.children.length, 4); i++) {
          const c = root.children[i];
          if (c?.content?.uri) urls.push(toAbs(c.content.uri));
        }
      }
      await Promise.allSettled(urls.map(u => fetch(u, { headers })));
    } catch (err) {
      // Silent fail; prefetch is best-effort
    }
  }, []);

  // Warm cache for neighbor versions whenever selection changes
  useEffect(() => {
    if (!show3DModel || !selectedTilesetAsset || !available3dModelVersions?.available || !selected3dModelVersion) return;
    const list = available3dModelVersions.available;
    const idx = list.findIndex(v => v === selected3dModelVersion);
    if (idx === -1) return;
    const neighbors = [list[idx - 1], list[idx + 1]].filter(Boolean);
    neighbors.forEach(v => prefetchTilesetResources(selectedTilesetAsset, v));
  }, [show3DModel, selectedTilesetAsset, available3dModelVersions, selected3dModelVersion, prefetchTilesetResources]);

  // Add function to close/disable 3D model view
  const handleClose3DModel = () => {
    console.log("Closing 3D model view");
    
    // First disable the 3D model view
    setShow3DModel(false);
    
    // Then clear the selected asset and version info
    setSelectedTilesetAsset(null);
    setAvailable3dModelVersions({ latest: null, available: [] });
    setSelected3dModelVersion(null);
    
    // Show notification
    setSnackbarMessage('3D model view closed');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  // Handler for 3D tileset loading errors
  const handleTilesetLoadError = useCallback((errorMessage) => {
    setSnackbarMessage(errorMessage);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
    
    // Automatically close the 3D view if there was an error
    setShow3DModel(false);
    setSelectedTilesetAsset(null);
  }, []);

  const handleTilesetReady = useCallback((tileset) => {
   console.log('[DEBUG] handleTilesetReady triggered. isInitialModelLoad:', isInitialModelLoad);
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer || !tileset) return;

    // Show success message every time a model loads
    setSnackbarMessage('3D model loaded successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);

    // But ONLY zoom the camera on the very first load
    if (isInitialModelLoad && isInitialModelLoad.current) {
      console.log('Initial tileset load, positioning camera directly above model');
      
      // Set a slight delay to ensure the tileset is fully processed
      setTimeout(() => {
        try {
          // Get the tileset's bounding sphere for proper centering
          const boundingSphere = tileset.boundingSphere;
          if (boundingSphere) {
            // Log bounding sphere info for debugging
            console.log('Tileset bounding sphere:', {
              center: boundingSphere.center,
              radius: boundingSphere.radius
            });
            
            const radius = boundingSphere.radius;
            
            // Calculate distance to position camera directly above the model
            // The distance should be enough to see the entire model from above
            let distance;
            if (radius < 1) {
              // Very small models (< 1m radius) - use minimum distance
              distance = 30;
              console.log('Small model detected, using minimum overhead distance:', distance);
            } else if (radius > 1000) {
              // Very large models (> 1km radius) - use larger multiplier for aerial view
              distance = radius * 3.0; // Larger multiplier for proper aerial overview
              console.log('Large model detected, using large overhead distance:', distance);
            } else {
              // Normal sized models - use multiplier for good aerial view
              distance = radius * 5.0; // Good multiplier for overhead view
              console.log('Normal model size, using standard overhead distance:', distance);
            }
            
            // Ensure reasonable distance range for aerial view
            distance = Math.max(Math.min(distance, 8000), 25); // Max 8km, min 25m
            
            console.log('Final overhead camera distance:', distance);
            
            // Position camera directly above the model facing straight down
            viewer.camera.flyToBoundingSphere(boundingSphere, {
              duration: 2.5, // Smooth animation
              offset: new Cesium.HeadingPitchRange(
                0,                               // Heading: 0 = North (no rotation)
                Cesium.Math.toRadians(-90),      // Pitch: -90° = Looking straight down
                distance                         // Distance above the model
              ),
              complete: () => {
                console.log('Camera positioned directly above 3D model');
                // Add a post-positioning check after the animation completes
                setTimeout(() => {
                  checkAndAdjustModelVisibility(viewer, tileset, boundingSphere);
                }, 500);
                // Mark initial load handled to avoid repeated re-centering
                if (isInitialModelLoad) {
                  isInitialModelLoad.current = false;
                }
              }
            });
            
          } else {
            console.warn('No bounding sphere available, using asset coordinates for overhead view');
            
            // Fallback using asset coordinates with overhead positioning
            if (selectedTilesetAsset && selectedTilesetAsset.center_lat && selectedTilesetAsset.center_lng) {
              const assetElevation = parseFloat(selectedTilesetAsset.center_elevation || 0);
              
              // Calculate position directly above the asset
              const overheadPosition = Cesium.Cartesian3.fromDegrees(
                parseFloat(selectedTilesetAsset.center_lng),
                parseFloat(selectedTilesetAsset.center_lat),
                assetElevation + 200 // Position 200m above the asset
              );
              
              viewer.camera.flyTo({
                destination: overheadPosition,
                orientation: {
                  heading: 0,                          // North-aligned
                  pitch: Cesium.Math.toRadians(-90),   // Looking straight down
                  roll: 0
                },
                duration: 2.5,
                complete: () => {
                  console.log('Camera positioned above asset using coordinates');
                  // Check visibility after positioning
                  setTimeout(() => {
                    checkAndAdjustModelVisibility(viewer, tileset, null);
                  }, 500);
                  // Mark initial load handled to avoid repeated re-centering
                  if (isInitialModelLoad) {
                    isInitialModelLoad.current = false;
                  }
                }
              });
            } else {
              // Final fallback using basic flyTo with overhead view
              console.log('Using basic flyTo with overhead view as final fallback');
              viewer.camera.flyTo(tileset, {
                duration: 2.5,
                offset: new Cesium.HeadingPitchRange(
                  0,                               // North-aligned
                  Cesium.Math.toRadians(-90),      // Looking straight down
                  200                              // Conservative default distance
                ),
                complete: () => {
                  console.log('Camera positioned using fallback overhead method');
                  // Check visibility after positioning
                  setTimeout(() => {
                    checkAndAdjustModelVisibility(viewer, tileset, tileset.boundingSphere);
                  }, 500);
                  // Mark initial load handled to avoid repeated re-centering
                  if (isInitialModelLoad) {
                    isInitialModelLoad.current = false;
                  }
                }
              });
            }
          }
          
        } catch (error) {
          console.error('Error zooming to 3D model:', error);
          
          // Ultimate fallback positioning
          try {
            console.log('Attempting ultimate fallback camera positioning');
            
            // Try to use the asset coordinates directly
            if (selectedTilesetAsset && selectedTilesetAsset.center_lat && selectedTilesetAsset.center_lng) {
              const fallbackPosition = Cesium.Cartesian3.fromDegrees(
                parseFloat(selectedTilesetAsset.center_lng),
                parseFloat(selectedTilesetAsset.center_lat),
                parseFloat(selectedTilesetAsset.center_elevation || 0) + 100
              );
              
              viewer.camera.flyTo({
                destination: fallbackPosition,
                orientation: {
                  heading: Cesium.Math.toRadians(0),   // North facing for fallback
                  pitch: Cesium.Math.toRadians(-30),  // Moderate angle
                  roll: 0
                },
                duration: 2
              });
              
              setSnackbarMessage('3D model loaded (fallback positioning)');
              setSnackbarSeverity('warning');
              setSnackbarOpen(true);
            } else {
              // Last resort: just try to fly to the tileset without custom parameters
              viewer.camera.flyTo(tileset, { duration: 2 });
              
              setSnackbarMessage('3D model loaded (basic positioning)');
              setSnackbarSeverity('warning');
              setSnackbarOpen(true);
            }
          } catch (fallbackError) {
            console.error('All camera positioning methods failed:', fallbackError);
            setSnackbarMessage('3D model loaded but positioning failed');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          }
        }
      }, 750); // Increased delay to ensure tileset is fully ready
    }
    
    // Helper function to check and adjust model visibility (preserves current camera orientation)
    const checkAndAdjustModelVisibility = (viewer, tileset, boundingSphere) => {
      try {
        console.log('Checking model visibility and adjusting if needed...');
        
        // Get current camera position and direction
        const camera = viewer.camera;
        const cameraPosition = camera.position.clone();
        const currentHeading = camera.heading;
        const currentPitch = camera.pitch; // Preserve whatever pitch is currently set (e.g., -90°)
        
        // If we have a bounding sphere, check if the model center is in view
        if (boundingSphere) {
          const modelCenter = boundingSphere.center;
          const modelRadius = boundingSphere.radius;
          
          // Calculate distance from camera to model center
          const distanceToModel = Cesium.Cartesian3.distance(cameraPosition, modelCenter);
          
          console.log(`Model visibility check: distance=${distanceToModel.toFixed(1)}m, radius=${modelRadius.toFixed(1)}m`);
          
          // Check if we're too close or too far
          const optimalDistance = Math.max(modelRadius * 3.5, 25);
          const tolerance = optimalDistance * 0.3; // 30% tolerance
          
          if (distanceToModel < optimalDistance - tolerance || distanceToModel > optimalDistance + tolerance) {
            console.log(`Adjusting camera distance from ${distanceToModel.toFixed(1)}m to optimal ${optimalDistance.toFixed(1)}m`);
            
            // Adjust camera position while preserving current heading/pitch (avoid tilting away)
            viewer.camera.flyToBoundingSphere(boundingSphere, {
              duration: 1.5,
              offset: new Cesium.HeadingPitchRange(
                currentHeading,
                currentPitch,
                optimalDistance
              )
            });
            
            console.log('Camera position adjusted for better model visibility');
          } else {
            console.log('Model positioning is satisfactory');
          }
        } else {
          console.log('No bounding sphere available for visibility check');
        }
      } catch (error) {
        console.error('Error in model visibility check:', error);
      }
    };
  }, [viewerRef, selectedTilesetAsset, isInitialModelLoad]);

  // Add handler for clicking a photo marker on the map
  const handlePhotoEntityClick = useCallback((entity) => {
    if (!entity || !entity.id.startsWith('inspection-photo-')) return;
    
    let photoUrl = entity.photoUrl;
    // The Enhanced_ImageViewerModal expects a raw S3 key (e.g., 'uploads/...'),
    // but the entity's photoUrl is prefixed with a '/' for the InfoBox preview.
    // We must remove the leading slash before passing it to the modal.
    if (photoUrl && photoUrl.startsWith('/')) {
      photoUrl = photoUrl.substring(1);
    }
    const photoCaption = entity.name;
    const inspectionTitle = entity.inspectionTitle;
    const inspectionId = entity.inspectionId;
    
    console.log('Photo clicked:', { photoUrl, photoCaption, inspectionTitle, inspectionId });
    
    if (photoUrl) {
      const photoData = {
        url: photoUrl,
        caption: photoCaption,
        title: inspectionTitle,
        originalPath: entity.originalPhotoPath,
        inspectionId: inspectionId,
        id: entity.id.replace('inspection-photo-', '')
      };
      
      // For single photo selection from map, create array with just this photo
      setSelectedPhoto(photoData);
      setPhotoArray([photoData]); // Single photo array
      setCurrentPhotoIndex(0); // First (and only) photo
      setIsPhotoViewOnly(true); // For photos clicked on map, it's view-only
      setShowPhotoViewer(true); // Use setShowPhotoViewer instead of setPhotoModalOpen
    }
  }, []);

  // Handle plotting inspection images on the map
  const handlePlotInspectionImages = useCallback(async (photoDataArray, inspectionId) => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer || !photoDataArray || photoDataArray.length === 0) return;
    
    try {
      // First clear any existing plotted inspection photos
      const existingEntities = viewer.entities.values.filter(
        entity => entity.id.startsWith('inspection-photo-')
      );
      
      existingEntities.forEach(entity => {
        viewer.entities.remove(entity);
      });
      
      // Create an entity collection to track added entities
      const addedEntities = [];
      
      // Check if terrain is enabled and not ellipsoid
      const isTerrainEnabled = terrainEnabled &&
        viewer.terrainProvider &&
        !(viewer.terrainProvider instanceof Cesium.EllipsoidTerrainProvider);
      
      // If terrain is enabled, sample terrain heights for all photos first
      let terrainSampledPositions = null;
      if (isTerrainEnabled) {
        console.log('Terrain enabled - sampling terrain heights for photo positioning');
        try {
          // Create cartographic positions for all photos
          const cartographicPositions = photoDataArray.map(photoData =>
            Cesium.Cartographic.fromDegrees(
              photoData.longitude,
              photoData.latitude,
              0
            )
          );
          
          // Sample terrain heights for all positions
          terrainSampledPositions = await Cesium.sampleTerrainMostDetailed(
            viewer.terrainProvider,
            cartographicPositions
          );
          
          console.log('Terrain sampling completed for photo positioning');
        } catch (error) {
          console.warn('Failed to sample terrain for photos, using altitude data:', error);
          terrainSampledPositions = null;
        }
      }
      
      // Plot all photos with geo data
      photoDataArray.forEach((photoData, index) => {
        // The photo_path from the API is now a clean, web-relative path like 'uploads/...'
        // We just need to ensure it's formatted correctly for the AuthenticatedImage component.
        let photoPath = photoData.photoPath.replace(/\\/g, '/');
        
        // The AuthenticatedImage component (used in the modal) expects a path starting with `/uploads/...`
        // It will be automatically prefixed with the API base URL by the axios instance.
        if (!photoPath.startsWith('/')) {
          photoPath = '/' + photoPath;
        }
        
        // The photoUrl for the modal needs to be this relative path.
        // The description's <img> tag will also use this, but it might fail to load
        // because it's not an authenticated request. We prioritize fixing the modal first.
        const photoUrl = photoPath;
        
        // Extract filename for alternate URLs
        const filename = photoPath.split('/').pop();
        
        console.log('Adding photo to map:', {
          id: photoData.id,
          caption: photoData.caption,
          originalPath: photoData.photoPath,
          processedPath: photoPath,
          url: photoUrl,
          filename: filename,
          inspectionId: inspectionId,
          gimbalYaw: photoData.gimbalYaw,
          flightYaw: photoData.flightYaw
        });
        
        // Calculate proper altitude based on terrain
        let photoAltitude = photoData.altitude || 0;
        
        if (isTerrainEnabled && terrainSampledPositions && terrainSampledPositions[index]) {
          const terrainHeight = terrainSampledPositions[index].height || 0;
          
          // If photo has GPS altitude, use it relative to terrain
          // If no GPS altitude, place slightly above terrain surface
          if (photoData.altitude && photoData.altitude > 0) {
            // Use GPS altitude as absolute height (already accounts for terrain)
            photoAltitude = photoData.altitude;
          } else {
            // Place 2 meters above terrain surface for visibility
            photoAltitude = terrainHeight + 2;
          }
          
          console.log(`Photo ${photoData.id}: terrain=${terrainHeight.toFixed(1)}m, GPS=${photoData.altitude || 'N/A'}m, final=${photoAltitude.toFixed(1)}m`);
        }
        
        // Build description HTML with drone metadata if available
        let descriptionHtml = `
          <h3>${photoData.caption}</h3>
          <p>Inspection: ${photoData.inspectionTitle || 'N/A'}</p>
          <p>Location: ${photoData.latitude.toFixed(6)}, ${photoData.longitude.toFixed(6)}</p>
          <p>Altitude: ${photoData.altitude ? photoData.altitude.toFixed(1) + ' m' : 'N/A'}</p>
          <p>Date: ${photoData.takenAt ? new Date(photoData.takenAt).toLocaleString() : 'N/A'}</p>
        `;
        
        // Add drone metadata to description if available
        if (photoData.isDronePhoto) {
          descriptionHtml += `
            <div style="margin-top: 10px; padding: 8px; background-color: #f0f8ff; border-radius: 4px;">
              <h4 style="margin-top: 0;">Drone Data</h4>
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 2px 5px; font-weight: bold;">Flight:</td>
                  <td style="padding: 2px 5px;">
                    Yaw: ${typeof photoData.flightYaw === 'number' ? photoData.flightYaw.toFixed(1) + '°' : 'N/A'},
                    Pitch: ${typeof photoData.flightPitch === 'number' ? photoData.flightPitch.toFixed(1) + '°' : 'N/A'},
                    Roll: ${typeof photoData.flightRoll === 'number' ? photoData.flightRoll.toFixed(1) + '°' : 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style="padding: 2px 5px; font-weight: bold;">Gimbal:</td>
                  <td style="padding: 2px 5px;">
                    Yaw: ${typeof photoData.gimbalYaw === 'number' ? photoData.gimbalYaw.toFixed(1) + '°' : 'N/A'},
                    Pitch: ${typeof photoData.gimbalPitch === 'number' ? photoData.gimbalPitch.toFixed(1) + '°' : 'N/A'},
                    Roll: ${typeof photoData.gimbalRoll === 'number' ? photoData.gimbalRoll.toFixed(1) + '°' : 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style="padding: 2px 5px; font-weight: bold;">Speed:</td>
                  <td style="padding: 2px 5px;">${typeof photoData.flightSpeed === 'number' ? photoData.flightSpeed.toFixed(1) + ' m/s' : 'N/A'}</td>
                </tr>
                <tr>
                  <td style="padding: 2px 5px; font-weight: bold;">Drone:</td>
                  <td style="padding: 2px 5px;">${photoData.droneMake || ''} ${photoData.droneModel || 'Unknown'}</td>
                </tr>
              </table>
            </div>
          `;
        }
        
        // Add the image at the end of the description
        descriptionHtml += `<img src="${photoUrl}" style="max-width: 100%; max-height: 200px; margin-top: 10px;" />`;
        
        // Create the photo entity with terrain-aware positioning
        const entity = viewer.entities.add({
          id: `inspection-photo-${photoData.id}`,
          name: photoData.caption,
          description: descriptionHtml,
          position: Cartesian3.fromDegrees(
            photoData.longitude,
            photoData.latitude,
            photoAltitude
          ),
          // Replace simple point with sleeker design
          point: {
            // Use conditional color based on whether the photo has annotations
            color: Cesium.Color.fromCssColorString(
              photoData.has_annotation ? '#FF073A' : '#39FF14'  // Neon Red if has annotations, Neon Green otherwise
            ).withAlpha(0.9),
            pixelSize: photoData.has_annotation ? 10 : 8, // Slightly larger for annotated photos
            outlineColor: Cesium.Color.WHITE, // White outline for better visibility
            outlineWidth: 2, // Crisp outline
            heightReference: isTerrainEnabled ? Cesium.HeightReference.NONE : Cesium.HeightReference.RELATIVE_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new Cesium.NearFarScalar(500, 1.0, 15000, 0.6), // Scale down when far away
            translucencyByDistance: new Cesium.NearFarScalar(500, 1.0, 15000, 0.7) // More transparent when far away
          },
          // Add custom properties for modal display
          photoUrl: photoUrl,
          originalPhotoPath: photoData.photoPath,
          processedPhotoPath: photoPath,
          filename: filename,
          inspectionTitle: photoData.inspectionTitle,
          inspectionId: inspectionId
        });
        
        // Add a direction arrow for gimbal direction if gimbal yaw is available
        if (photoData.gimbalYaw !== undefined && photoData.gimbalYaw !== null) {
          // Calculate the forward direction based on gimbal yaw (convert to radians)
          // Note: gimbalYaw is in degrees, with 0 being North, positive clockwise
          const gimbalYawRadians = Cesium.Math.toRadians(photoData.gimbalYaw);
          
          // Also consider pitch if available (typically negative for drones pointing down)
          // Default to -45 degrees if pitch is not available
          const gimbalPitch = (photoData.gimbalPitch !== undefined && photoData.gimbalPitch !== null)
            ? photoData.gimbalPitch
            : -45;
          const gimbalPitchRadians = Cesium.Math.toRadians(gimbalPitch);
          
          // Create arrow pointing in the gimbal direction
          // The arrow is a line from the drone position extending in the direction of the gimbal
          const arrowLength = 10; // Length of the arrow in meters
          
          // Calculate the horizontal distance based on pitch
          // At -90 degrees (straight down), horizontal distance is 0
          // At 0 degrees (straight ahead), horizontal distance is full arrowLength
          const horizontalDistance = arrowLength * Math.cos(Math.abs(gimbalPitchRadians));
          
          // Calculate end point of the arrow using trigonometry for horizontal component
          const endLongitude = photoData.longitude +
            (horizontalDistance / (111320 * Math.cos(Cesium.Math.toRadians(photoData.latitude)))) *
            Math.sin(gimbalYawRadians);
            
          const endLatitude = photoData.latitude +
            (horizontalDistance / 111320) * Math.cos(gimbalYawRadians);
          
          // Calculate vertical component based on pitch
          // At -90 degrees (straight down), vertical difference is -arrowLength
          // At 0 degrees (straight ahead), vertical difference is 0
          const verticalDistance = arrowLength * Math.sin(gimbalPitchRadians);
          
          // Use terrain-aware altitude for arrow positioning
          const startAltitude = photoAltitude;
          const endAltitude = startAltitude + verticalDistance;
          
          // Add the arrow as a polyline entity
          const arrowEntity = viewer.entities.add({
            id: `inspection-photo-arrow-${photoData.id}`,
            polyline: {
              positions: Cesium.Cartesian3.fromDegreesArrayHeights([
                photoData.longitude, photoData.latitude, startAltitude,
                endLongitude, endLatitude, endAltitude
              ]),
              width: 4, // Thinner line for sleeker appearance
              material: new Cesium.PolylineArrowMaterialProperty(
                // Match the point color (either neon red or neon green)
                Cesium.Color.fromCssColorString(photoData.has_annotation ? '#FF073A' : '#39FF14').withAlpha(0.85)
              ),
              clampToGround: false,
              heightReference: isTerrainEnabled ? Cesium.HeightReference.NONE : Cesium.HeightReference.RELATIVE_TO_GROUND
            }
          });
          
          // Add the arrow entity to the collection of added entities
          addedEntities.push(arrowEntity);
        }
        
        addedEntities.push(entity);
      });
      
      // If there are photos, fly to view all of them
      if (photoDataArray.length > 0) {
        // Calculate a bounding sphere that contains all entities
        const boundingSphere = Cesium.BoundingSphere.fromPoints(
          addedEntities.map(entity => {
            // For polyline entities, use the first position
            if (entity.polyline) {
              // Instead of trying to access x,y,z directly, get the Cartesian3 properly
              const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now());
              if (positions && positions.length > 0) {
                return positions[0];
              }
              return null;
            }
            // For other entities, use their position
            return entity.position.getValue(Cesium.JulianDate.now());
          }).filter(position => position !== null) // Filter out any null positions
        );
        
        // Check if boundingSphere has a valid radius before using it
        if (boundingSphere && boundingSphere.radius > 0 && boundingSphere.radius < 1000000) {
          // Add some padding to the bounding sphere - reduced multiplier to avoid excessive zoom
          boundingSphere.radius *= 1.2; // Reduced from 1.5
          
          // Fly the camera to the bounding sphere with a nice animation
          viewer.camera.flyToBoundingSphere(boundingSphere, {
            duration: 1.5,
            offset: new Cesium.HeadingPitchRange(0, -Math.PI/4, 0) // Look from above at an angle
          });
        } else {
          // Fallback to just flying to the first photo
          if (photoDataArray[0]) {
            const firstPhoto = photoDataArray[0];
            viewer.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(
                firstPhoto.longitude,
                firstPhoto.latitude,
                (firstPhoto.altitude || 0) + 100 // Add 100m height for better view
              ),
              orientation: {
                heading: 0,
                pitch: Cesium.Math.toRadians(-45), // Look down at 45 degrees
                roll: 0
              },
              duration: 1.5
            });
          }
        }
        
        // Show notification
        setSnackbarMessage(`Plotted ${photoDataArray.length} inspection photos on map`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Set up click handler on the viewer if not already set
        if (!viewer.screenSpaceEventHandler) {
          viewer.screenSpaceEventHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        }
        
        // Add click event handler for entities
        viewer.screenSpaceEventHandler.setInputAction((click) => {
          const pickedObject = viewer.scene.pick(click.position);
          if (defined(pickedObject) && pickedObject.id) {
            handlePhotoEntityClick(pickedObject.id);
          }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      }
    } catch (error) {
      console.error('Error plotting inspection images:', error);
      setSnackbarMessage('Failed to plot inspection photos on map');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [viewerRef, terrainEnabled, handlePhotoEntityClick]);

  // Handle removing plotted inspection images from map
  const handleRemoveInspectionImages = useCallback((inspectionId) => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;
    
    try {
      // Remove entities for this specific inspection
      const entitiesToRemove = viewer.entities.values.filter(
        entity => entity.id.startsWith('inspection-photo-')
      );
      
      entitiesToRemove.forEach(entity => {
        viewer.entities.remove(entity);
      });
      
      // Show notification
      setSnackbarMessage('Removed inspection photos from map');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      
      // Force a refresh of the inspection panel to update button states
      setInspectionRefreshKey(prevKey => prevKey + 1);
    } catch (error) {
      console.error('Error removing inspection images:', error);
      setSnackbarMessage('Failed to remove inspection photos from map');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [viewerRef]);

  // Handler for closing the photo modal
  const handleClosePhotoModal = useCallback(() => {
    setShowPhotoViewer(false);
    setSelectedPhoto(null);
    setIsPhotoViewOnly(false);
    // Clear navigation state
    setPhotoArray([]);
    setCurrentPhotoIndex(0);

    // If the photo viewer was opened from inside the InspectionDetailsModal,
    // return the user to that modal. selectedInspection is preserved across the
    // photo-viewer round trip (handlePhotoSelect doesn't clear it), so reopening
    // the inspection modal renders the same inspection.
    if (resumeInspectionAfterPhotoRef.current) {
      resumeInspectionAfterPhotoRef.current = false;
      setShowInspectionGallery(true);
    }
  }, []);

  // Handler for photo navigation
  const handlePhotoNavigation = useCallback((newIndex) => {
    if (photoArray.length > 0 && newIndex >= 0 && newIndex < photoArray.length) {
      setCurrentPhotoIndex(newIndex);
      setSelectedPhoto(photoArray[newIndex]);
    }
  }, [photoArray]);

  // Enhanced photo selection handler that supports navigation
  const handlePhotoSelectWithNavigation = useCallback((selectedPhoto, photoArray = [], photoIndex = 0) => {
    console.log('Photo selected with navigation:', { selectedPhoto, photoArray: photoArray.length, photoIndex });
    
    // Set the selected photo and navigation data
    setSelectedPhoto(selectedPhoto);
    setPhotoArray(photoArray);
    setCurrentPhotoIndex(photoIndex);
    
    // Determine if it's view-only (for now, assume it's editable from inspection details)
    setIsPhotoViewOnly(false);
    
    // Show the photo viewer
    setShowPhotoViewer(true);
  }, []);

  // Handler for closing the inspection panel (for the X button in InspectionPanel)
  const handleCloseInspectionPanel = useCallback(() => {
    setShowInspectionPanel(false);
    setInspectionAsset(null);
    
    // Also remove any plotted images from the map when closing the panel
    const viewer = viewerRef.current?.cesiumElement;
    if (viewer) {
      try {
        // Remove all inspection photo entities
        const entitiesToRemove = viewer.entities.values.filter(
          entity => entity.id.startsWith('inspection-photo-')
        );
        
        if (entitiesToRemove.length > 0) {
          console.log(`Removing ${entitiesToRemove.length} plotted images from map`);
          entitiesToRemove.forEach(entity => {
            viewer.entities.remove(entity);
          });
          
          // Show notification
          setSnackbarMessage('Removed inspection photos from map');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          
          // Force a refresh of the inspection panel to update button states
          setInspectionRefreshKey(prevKey => prevKey + 1);
        }
      } catch (error) {
        console.error('Error removing inspection images:', error);
      }
    }
  }, [viewerRef]);

  // Handler for entering full-screen inspection mode
  const handleEnterInspectionFullScreen = useCallback(() => {
    setInspectionFullScreenMode(true);
  }, []);

  // Handler for exiting full-screen inspection mode
  const handleExitInspectionFullScreen = useCallback(() => {
    setInspectionFullScreenMode(false);
  }, []);

  // ESC key listener for exiting fullscreen inspection mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && inspectionFullScreenMode) {
        handleExitInspectionFullScreen();
      }
    };

    if (inspectionFullScreenMode) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [inspectionFullScreenMode, handleExitInspectionFullScreen]);

  // Sidebar resize handlers for fullscreen mode
  const handleSidebarResizeStart = useCallback((e) => {
    setIsResizingSidebar(true);
    e.preventDefault();
  }, []);

  const handleSidebarResizeMove = useCallback((e) => {
    if (!isResizingSidebar) return;
    // Constrain width between 280px min and 600px max
    const newWidth = Math.max(280, Math.min(600, e.clientX));
    setFullscreenSidebarWidth(newWidth);
  }, [isResizingSidebar]);

  const handleSidebarResizeEnd = useCallback(() => {
    setIsResizingSidebar(false);
  }, []);

  // Mouse event listeners for sidebar resizing
  useEffect(() => {
    if (isResizingSidebar) {
      document.addEventListener('mousemove', handleSidebarResizeMove);
      document.addEventListener('mouseup', handleSidebarResizeEnd);
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleSidebarResizeMove);
      document.removeEventListener('mouseup', handleSidebarResizeEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizingSidebar, handleSidebarResizeMove, handleSidebarResizeEnd]);

  // Add this handler function
  // Handler for toggling asset footprint visibility
  const handleToggleFootprint = (asset) => {
    const assetId = asset?.id || asset?.assetId;
    const currentAssetId = selectedFootprintAsset?.id || selectedFootprintAsset?.assetId;
    
    console.log(`Footprint toggle clicked for asset: ${assetId}`);
    
    // Check if the footprint for THIS asset is currently shown
    if (isFootprintVisible && currentAssetId === assetId) {
      // If yes, hide it (toggle off)
      console.log(`Toggling OFF footprint for asset: ${assetId}`);
      setIsFootprintVisible(false);
      setSelectedFootprintAsset(null);
    } else {
      // If not, show it (toggle on)
      console.log(`Toggling ON footprint for asset: ${assetId}`);
      setSelectedFootprintAsset(asset);
      setIsFootprintVisible(true);
    }
  };

  // Add handler for batch upload button
  const handleBatchUploadOpen = () => {
    // Check if user can edit (upload batches)
    if (!canEdit) {
      setSnackbarMessage('You do not have permission to upload batch data.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    setBatchUploadModalOpen(true);
  };
  
  // Function to fetch defect types for the modal (declare before we use it)
  const fetchDefectTypes = useCallback(async () => {
    if (!userOrganization?.id) return;

    setLoadingDefectTypes(true);
    try {
      const response = await classificationApi.getDefectTypes({
        organization_id: userOrganization.id,
        asset_type_id: 1 // Road asset type
      });
      setDefectTypesList(response.data || []);
    } catch (err) {
      console.error('Failed to fetch defect types:', err);
    } finally {
      setLoadingDefectTypes(false);
    }
  }, [userOrganization?.id]);

  // Add handlers for DefectValidationModal in Operation Mode
  const handleDefectMarkerClick = useCallback((defectData) => {
    console.log('Defect marker clicked:', defectData);
    // Transform defect data to match DefectValidationModal expected format
    const defectForModal = {
      id: defectData.id,
      recordType: defectData.recordType,
      defectId: defectData.defectId,
      defectType: defectData.defectType,
      confidence: defectData.confidence,
      detectedAt: defectData.detectedAt,
      location: [defectData.networkName, defectData.sectionName, defectData.chainageName].filter(Boolean).join(' • ') || 'Out of Boundary',
      rawData: defectData.rawData,
      originalBoundingBoxId: defectData.rawData?.original_bounding_box_id,
      validatedBy: defectData.rawData?.validated_by_name,
      validatedAt: defectData.rawData?.validated_at,
    };
    setSelectedDefectForValidation(defectForModal);
    setDefectValidationModalOpen(true);

    // Fetch defect types if not already loaded
    if (defectTypesList.length === 0) {
      fetchDefectTypes();
    }
  }, [defectTypesList.length, fetchDefectTypes]);

  const handleDefectModalClose = () => {
    setDefectValidationModalOpen(false);
    setSelectedDefectForValidation(null);
  };

  // Handler for closing the batch upload modal
  const handleBatchUploadClose = () => {
    setBatchUploadModalOpen(false);
  };

  // Add handlers for OperationFilterPanel
  const toggleOperationFilterPanel = () => {
    setShowOperationFilterPanel(prevState => !prevState);
  };

  const handleOperationFilterChange = (filterType, value) => {
    setOperationFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: value
    }));
  };

  const handleOperationFilterReset = () => {
    setOperationFilters({
      defectTypeId: '',
      deviceId: '',
      networkId: '',
      sectionId: '',
      region: '',
      validationStatus: '',
      priority: '',
      dateRange: 'today',
      customDateFrom: '',
      customDateTo: '',
      searchTerm: ''
    });
  };

  // Handler for OperationFilterManager
  const handleOperationFilterManagerChange = (filters) => {
    console.log('Operation Filter Manager filters changed:', filters);
    setOperationFilterManagerFilters(filters);
    // Apply filters to operation defects here if needed
    // This would be where you'd filter the operationDefects based on the filters
  };

  const theme = useTheme();

  // Function to handle Home button click
  const handleHomeClick = () => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;
    
    // Reset camera to home position with animation
    viewer.camera.flyTo({
      destination: homeCameraPosition,
      duration: 1.5, // Animation duration in seconds
    });
    
    // Show notification
    setSnackbarMessage('Camera view reset to home position');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  // Handler for switching terrain providers
  const handleTerrainProviderChange = async (providerId) => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer || terrainLoading) return;
    
    setTerrainLoading(true);
    try {
      // Clean up old provider
      if (viewer.terrainProvider) {
        cleanupTerrainProvider(viewer.terrainProvider);
      }
      
      const terrainProvider = await createTerrainProvider(providerId);
      if (terrainProvider) {
        viewer.terrainProvider = terrainProvider;
        setCurrentTerrainProvider(providerId);
        setTerrainEnabled(true);
        
        // Optimize terrain performance
        optimizeTerrainPerformance(viewer);
        
        // Show notification
        const providerConfig = mapConfig.terrain.providers.find(p => p.id === providerId);
        setSnackbarMessage(`Switched to ${providerConfig?.name || providerId} terrain`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        console.log(`MapViewPage: Switched to terrain provider: ${providerId}`);
      }
    } catch (error) {
      console.error('Failed to switch terrain provider:', error);
      setSnackbarMessage(`Failed to load ${providerId} terrain`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Fallback to ellipsoid on error
      viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      setCurrentTerrainProvider('ellipsoid');
      setTerrainEnabled(true);
    } finally {
      setTerrainLoading(false);
    }
  };
  
  // Toggle terrain on/off
  const handleTerrainToggle = async () => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer || terrainLoading) return;
    
    if (terrainEnabled) {
      // Switch to ellipsoid (disable terrain)
      if (viewer.terrainProvider) {
        cleanupTerrainProvider(viewer.terrainProvider);
      }
      viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      setTerrainEnabled(false);
      
      setSnackbarMessage('Terrain disabled - using flat ellipsoid');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } else {
      // Re-enable previous terrain provider
      await handleTerrainProviderChange(currentTerrainProvider || mapConfig.terrain.defaultProvider);
    }
  };

  // Function to calculate date range based on filter selection
  const calculateDateRange = (dateRange, customDateFrom, customDateTo) => {
    // 🔧 FIX: Use user's local date to correctly determine "today"
    const localDate = new Date();
    
    // Helper function to format a date object into YYYY-MM-DD in the local timezone
    const toLocalISOString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = toLocalISOString(localDate);

    let fromDate, toDate;
    
    switch (dateRange) {
      case 'yesterday':
        const yesterday = new Date(localDate);
        yesterday.setDate(localDate.getDate() - 1);
        fromDate = toDate = toLocalISOString(yesterday);
        break;
        
      case 'last7days':
        const weekAgo = new Date(localDate);
        weekAgo.setDate(localDate.getDate() - 7);
        fromDate = toLocalISOString(weekAgo);
        toDate = today;
        break;
        
      case 'last30days':
        const monthAgo = new Date(localDate);
        monthAgo.setDate(localDate.getDate() - 30);
        fromDate = toLocalISOString(monthAgo);
        toDate = today;
        break;
        
      case 'custom':
        fromDate = customDateFrom || today;
        toDate = customDateTo || today;
        break;
        
      case 'today':
      default:
        fromDate = toDate = today;
        break;
    }
    
    console.log('[DEBUG] Returning dates:', { fromDate, toDate });
    return { fromDate, toDate };
  };

  // Updated function to fetch defects with date range support (renamed from fetchTodayDefects)
  const fetchOperationDefects = useCallback(async (filters = {}) => {
    if (!userOrganization?.id) {
      console.log('[MapViewPage] No organization ID available for Operation Mode defects fetch');
      return;
    }

    setLoadingOperationDefects(true);
    
    // Calculate date range based on filters
    const { fromDate, toDate } = calculateDateRange(
      filters.dateRange || 'today',
      filters.customDateFrom,
      filters.customDateTo
    );

    const { validationStatus, actionStatus } = filters;
    
    console.log('[MapViewPage] Fetching defects/findings for Operation Mode', {
      dateRange: filters.dateRange || 'today',
      fromDate,
      toDate,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    try {
      // Use higher limit for Operation Mode to match asset visualization standards
      // Check mapConfig for consistent limits
      const operationModeLimit = 10000; // Increased from 1000 to 10000 for better coverage
      
      const params = {
        limit: operationModeLimit,
        offset: 0,
        sort_by: 'first_detected_at',
        sort_order: 'DESC',
        from_date: fromDate,
        to_date: toDate, // Now uses dynamic date range!
        boundary_status: 'in_boundary' // ONLY load 'in_boundary' defects/findings
      };

      if (validationStatus) {
        params.validation_status = validationStatus;
      }
      if (actionStatus) {
        params.work_order_status = actionStatus;
      }

      console.log('[MapViewPage] Fetching defects with params:', params);

      // Log the exact parameters being sent to the API
      console.log('[DEBUG] API Request Params:', {
        from_date: params.from_date,
        to_date: params.to_date,
        limit: params.limit,
        offset: params.offset,
        sort_by: params.sort_by,
        sort_order: params.sort_order,
      });

      const response = await envisFindingsApi.getTableFindings(params);
      const rawFindings = response.data.data || [];

      console.log(`[MapViewPage] Retrieved ${rawFindings.length} findings/defects for date range ${fromDate} to ${toDate}`);
      console.log('[MapViewPage] Raw findings data:', rawFindings);
      
      // Check how many have coordinates
      const withCoordinates = rawFindings.filter(item => item.latitude && item.longitude);
      const withoutCoordinates = rawFindings.filter(item => !item.latitude || !item.longitude);
      
      console.log(`[MapViewPage] ${withCoordinates.length} findings have coordinates, ${withoutCoordinates.length} missing coordinates`);
      if (withoutCoordinates.length > 0) {
        console.log('[MapViewPage] Findings without coordinates:', withoutCoordinates.map(item => ({
          id: item.id,
          defect_type: item.defect_type_name,
          latitude: item.latitude,
          longitude: item.longitude,
          detected_at: item.first_detected_at
        })));
      }

      // Transform data for map plotting - only include items with valid coordinates
      const transformedDefects = withCoordinates
        .map(item => {
          const isValidatedDefect = item.record_type === 'confirmed_defect';
          const defectTypeName = item.validated_defect_type_name || item.defect_type_name || 'Unknown';
          
          return {
            id: item.id,
            recordType: item.record_type,
            defectId: item.defect_id,
            latitude: parseFloat(item.latitude),
            longitude: parseFloat(item.longitude),
            defectType: defectTypeName,
            validationStatus: isValidatedDefect ? 'validated' : (item.status || 'pending'),
            confidence: item.confidence_score || 0,
            detectedAt: item.first_detected_at,
            deviceName: item.device_name || item.device_uuid,
            vehicleId: item.vehicle_id,
            networkName: item.network_name,
            sectionName: item.section_name,
            chainageName: item.chainage_segment_name,
            isValidatedDefect: isValidatedDefect,
            priority: item.priority,
            rawData: item,
            defectTypeId: item.defect_type_id,
            deviceId: item.device_uuid,
            networkId: item.network_id,
            sectionId: item.section_id,
            region: item.region,
          };
        });

      console.log(`[MapViewPage] Transformed ${transformedDefects.length} defects for map plotting`);
      setOperationDefects(transformedDefects);
      setFilteredOperationDefects(transformedDefects);

      // Show success notification with counts
      const validatedCount = transformedDefects.filter(d => d.isValidatedDefect).length;
      const findingsCount = transformedDefects.filter(d => !d.isValidatedDefect).length;
      
      const dateRangeText = fromDate === toDate ? fromDate : `${fromDate} to ${toDate}`;
      
      // Check if we hit the API limit and might have more data
      const hitLimit = rawFindings.length === operationModeLimit;
      const limitWarning = hitLimit ? ` ⚠️ LIMIT REACHED - There may be more data available` : '';
      
      setSnackbarMessage(
        `Operation Mode: Loaded ${transformedDefects.length} defects/findings ` +
        `(${validatedCount} validated defects, ${findingsCount} pending findings) ` +
        (withoutCoordinates.length > 0 ? `- ${withoutCoordinates.length} excluded (no coordinates) ` : '') +
        `- Date Range: ${dateRangeText}${limitWarning}`
      );
      setSnackbarSeverity(hitLimit ? 'warning' : 'success');
      setSnackbarOpen(true);

    } catch (error) {
      console.error('[MapViewPage] Error fetching defects:', error);
      setSnackbarMessage('Failed to load defects for Operation Mode');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setOperationDefects([]);
      setFilteredOperationDefects([]);
    } finally {
      setLoadingOperationDefects(false);
    }
  }, [userOrganization?.id]);

  // Client-side date filtering function
  const isWithinDateRange = (detectedAt, dateRange, customDateFrom, customDateTo) => {
    if (!detectedAt) return true; // Include if no date
    
    const detectedDate = new Date(detectedAt);
    const { fromDate, toDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);
    
    const startOfDay = new Date(fromDate);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(toDate);
    endOfDay.setHours(23, 59, 59, 999);
    
    return detectedDate >= startOfDay && detectedDate <= endOfDay;
  };

  // Effect to fetch defects when switching to Operation Mode OR when filters change
  useEffect(() => {
    if (selectedTab === 1 && isAuthenticated && userOrganization?.id) {
      console.log('[MapViewPage] Operation Mode activated or filters changed - fetching defects');
      fetchOperationDefects(operationFilterManagerFilters);
    } else if (selectedTab === 0) {
      // Clear operation defects when switching to Asset Mode
      console.log('[MapViewPage] Asset Mode activated - clearing operation defects');
      setOperationDefects([]);
      setFilteredOperationDefects([]);
    }
  }, [selectedTab, isAuthenticated, userOrganization?.id, fetchOperationDefects, operationFilterManagerFilters?.dateRange, operationFilterManagerFilters?.customDateFrom, operationFilterManagerFilters?.customDateTo]);

  // Effect to handle defect entity clicks in Operation Mode
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer || selectedTab !== 1) return; // Only in Operation Mode

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    
    handler.setInputAction((event) => {
      const picked = viewer.scene.pick(event.position);
      if (Cesium.defined(picked) && Cesium.defined(picked.id)) {
        const entity = picked.id; // Cesium Entity object
        const entityIdStr = entity.id; // string id we assigned
        
        if (typeof entityIdStr === 'string' && entityIdStr.startsWith('defect-')) {
          const defectData = filteredOperationDefects.find(defect => `defect-${defect.recordType}-${defect.id}` === entityIdStr);
          if (defectData) {
            handleDefectMarkerClick(defectData);
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Cleanup handler on component unmount or when dependencies change
    return () => {
      if (handler && !handler.isDestroyed()) {
        handler.destroy();
      }
    };
  }, [selectedTab, filteredOperationDefects, handleDefectMarkerClick]);

  // Effect to apply organization-based filtering for non-admin users
  useEffect(() => {
    // Auto-apply organization filter for non-system-admin users
    if (isAuthenticated && !isSystemAdmin && userOrganization?.id && !currentFilters.organization_id) {
      console.log('Auto-applying organization filter for non-admin user:', userOrganization);
      const autoFilters = {
        ...currentFilters,
        organization_id: userOrganization.id
      };
      setCurrentFilters(autoFilters);
    }
  }, [isAuthenticated, isSystemAdmin, userOrganization, currentFilters]);

  // Apply filters from OperationFilterManager to defects list
  useEffect(() => {
    // If no filters, show all
    let filtered = operationDefects;
    const f = operationFilterManagerFilters;
    if (f && operationDefects.length > 0) {
      filtered = operationDefects.filter(d => {
        // Defect Type
        if (f.defectTypeId && String(f.defectTypeId) !== String(d.defectTypeId) && f.defectTypeId !== d.defectType) {
          return false;
        }
        // Device
        if (f.deviceId && f.deviceId !== d.deviceId && f.deviceId !== d.deviceName) {
          return false;
        }
        // Network
        if (f.networkId && f.networkId !== d.networkId && f.networkId !== d.networkName) {
          return false;
        }
        // Section
        if (f.sectionId && f.sectionId !== d.sectionId && f.sectionId !== d.sectionName) {
          return false;
        }
        // Region
        if (f.region && f.region !== d.region) {
          return false;
        }
        // Validation Status
        if (f.validationStatus && f.validationStatus !== d.validationStatus) {
          return false;
        }
        // Action Status (Work Order Status)
        if (f.actionStatus && f.actionStatus !== d.rawData?.latest_work_order_status) {
            // Special case for 'no_work_order'
            if (f.actionStatus === 'no_work_order' && (d.rawData?.work_order_count === 0 || d.rawData?.work_order_count == null)) {
                // continue
            } else if (f.actionStatus !== d.rawData?.latest_work_order_status) {
                return false;
            }
        }
        // Date Range Filtering - IMPLEMENTED!
        if (!isWithinDateRange(d.detectedAt, f.dateRange, f.customDateFrom, f.customDateTo)) {
          return false;
        }
        // Search Term
        if (f.searchTerm && f.searchTerm.trim()) {
          const searchLower = f.searchTerm.toLowerCase();
          const searchFields = [
            d.defectType,
            d.deviceName,
            d.networkName,
            d.sectionName,
            d.chainageName,
            String(d.id),
            d.validationStatus
          ].filter(Boolean);
          
          const matchesSearch = searchFields.some(field => 
            String(field).toLowerCase().includes(searchLower)
          );
          
          if (!matchesSearch) {
            return false;
          }
        }
        return true;
      });
    }
    setFilteredOperationDefects(filtered);
  }, [operationDefects, operationFilterManagerFilters]);

  // Effect to auto-zoom map to filtered defects in Operation Mode
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;

    // Only proceed if in Operation Mode, viewer is ready, and there are defects to show
    if (selectedTab !== 1 || !viewer || filteredOperationDefects.length === 0) {
      return;
    }

    // Get the Cesium entities corresponding to the filtered defects
    const targetEntities = filteredOperationDefects
      .map(defect => viewer.entities.getById(`defect-${defect.recordType}-${defect.id}`))
      .filter(entity => entity !== undefined); // Filter out any entities that might not be found

    if (targetEntities.length > 0) {
      console.log(`[MapViewPage] Auto-zooming to ${targetEntities.length} filtered defects.`);
      
      // Fly to the entities with a smooth animation and some padding
      viewer.flyTo(targetEntities, {
        duration: 1.5, // Animation duration in seconds
        offset: new Cesium.HeadingPitchRange(
          0, // Default heading
          -Cesium.Math.PI_OVER_FOUR, // Look down at a 45-degree angle
          0 // Cesium will calculate the optimal distance
        )
      });
    }
  }, [filteredOperationDefects, selectedTab, viewerRef]);

  // Effect to auto-zoom map to filtered assets in Asset Mode
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    const assetFromState = location.state?.selectedAsset;

    // Only proceed if in Asset Mode, viewer is ready, and there are assets to show.
    // CRITICAL: Do NOT auto-zoom if the user navigated here to see a specific asset.
    if (selectedTab !== 0 || !viewer || assets.length === 0 || assetFromState) {
      return;
    }

    // Get the Cesium entities corresponding to the filtered assets
    const targetEntities = assets
      .map(asset => viewer.entities.getById(`asset-${asset.id}`))
      .filter(entity => entity !== undefined); // Filter out any entities that might not be found

    if (targetEntities.length > 0) {
      console.log(`[MapViewPage] Auto-zooming to ${targetEntities.length} filtered assets.`);
      
      // Fly to the entities with a smooth animation and some padding
      viewer.flyTo(targetEntities, {
        duration: 1.5, // Animation duration in seconds
        offset: new Cesium.HeadingPitchRange(
          0, // Default heading
          -Cesium.Math.PI_OVER_FOUR, // Look down at a 45-degree angle
          0 // Cesium will calculate the optimal distance
        )
      });
    }
  }, [assets, selectedTab, viewerRef]);
  return (
    <>
      {/* Alert for users with no disciplines assigned */}
      {!hasAnyDiscipline && selectedTab === 0 && (
        <MuiAlert
          severity="warning"
          sx={{
            position: 'absolute',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            maxWidth: '90%',
            boxShadow: 3
          }}
        >
          You have no asset disciplines assigned. Please contact your administrator to gain access to assets.
        </MuiAlert>
      )}
    {/* Full-Screen Inspection Mode Container */}
    {inspectionFullScreenMode && (
      <Fade in={inspectionFullScreenMode}>
        <Box
          sx={{
            position: 'fixed',
            top: 64, // Below navbar
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            display: 'flex',
            bgcolor: 'background.default',
          }}
        >
          {/* Left Sidebar - Inspection Panel */}
          <Box
            sx={{
              width: fullscreenSidebarWidth,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
            }}
          >
            <InspectionPanel
              open={true}
              onClose={handleExitInspectionFullScreen}
              asset={inspectionAsset}
              onSelectInspection={handleSelectInspection}
              onAddInspection={handleAddInspection}
              onEditInspection={handleEditInspection}
              onPlotImages={handlePlotInspectionImages}
              onRemoveImages={handleRemoveInspectionImages}
              refreshKey={inspectionRefreshKey}
              isFullScreenMode={true}
            />
          </Box>

          {/* Resizable Divider */}
          <Box
            onMouseDown={handleSidebarResizeStart}
            sx={{
              width: 6,
              cursor: 'col-resize',
              bgcolor: isResizingSidebar ? 'primary.main' : 'divider',
              transition: isResizingSidebar ? 'none' : 'background-color 0.2s',
              flexShrink: 0,
              '&:hover': {
                bgcolor: 'primary.light',
              },
            }}
          />

          {/* Main Content Area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Exit Fullscreen Button */}
            <Tooltip title="Exit full-screen mode (ESC)">
              <IconButton
                onClick={handleExitInspectionFullScreen}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>

            {/* Main Content - Photo Viewer or Inspection Details */}
            {showPhotoViewer ? (
              // Photo Viewer - replaces main content when viewing a photo
              <Enhanced_ImageViewerModal
                open={true}
                onClose={() => setShowPhotoViewer(false)}
                photo={selectedPhoto}
                onDataMutated={triggerInspectionPanelRefresh}
                viewOnlyMode={isPhotoViewOnly}
                photos={photoArray}
                currentIndex={currentPhotoIndex}
                onNavigate={handlePhotoNavigation}
                fullScreenMode={true}
              />
            ) : selectedInspection ? (
              // Inspection Details
              <InspectionDetailsModal
                open={true}
                onClose={handleExitInspectionFullScreen}
                inspection={selectedInspection}
                onPhotoSelect={handlePhotoSelect}
                onDataMutated={triggerInspectionPanelRefresh}
                fullScreenMode={true}
              />
            ) : (
              // Empty state - no inspection selected
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  gap: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select an inspection from the timeline
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Click on an inspection card in the left panel to view its details
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
    )}

    {/* Map Container - Hidden when in fullscreen inspection mode */}
    {/* Using visibility instead of display:none to preserve Cesium's WebGL context */}
    <Box
      sx={{
        width: '100%',
        height: '100%',
        visibility: inspectionFullScreenMode ? 'hidden' : 'visible',
        pointerEvents: inspectionFullScreenMode ? 'none' : 'auto',
      }}
      className="cesium-container" // Styles managed by index.css
    >
      <Viewer
        ref={viewerRef} // Assign the ref
        full
        baseLayerPicker={false} // Keep default picker disabled
        // Default imagery provider will be Bing Aerial (Cesium default)
        // Or explicitly set an initial one:
        // imageryProvider={basemapOptions[0].providerFactory()}
        geocoder={false}
        homeButton={false}
        sceneModePicker={false}
        navigationHelpButton={false}
        animation={false}
        timeline={false}
        fullscreenButton={false}
        infoBox={false}
        selectionIndicator={false}
        navigationInstructionsInitiallyVisible={false}
      >
        {/* Render point entities for assets - only in Asset mode */}
        {/* Use rating colors when Geotechnical category (3) or Slope type (4) is selected */}
        {selectedTab === 0 && (
          <EntityPointLayer
            assets={assets}
            useRatingColors={
              currentFilters.category_id === '3' ||
              currentFilters.category_id === 3 ||
              currentFilters.type_id === '4' ||
              currentFilters.type_id === 4
            }
          />
        )}

        {/* Render defects/findings entities - only in Operation mode */}
        {selectedTab === 1 && filteredOperationDefects.length > 0 && (
          <>
            {filteredOperationDefects.map((defect) => (
              <Entity
                id={`defect-${defect.recordType}-${defect.id}`}
                key={`defect-${defect.recordType}-${defect.id}`}
                name={`${defect.isValidatedDefect ? 'Defect' : 'Finding'}: ${defect.defectType}`}
                description={`
                  <div style="font-family: Arial, sans-serif; max-width: 300px;">
                    <h3 style="margin: 0 0 10px 0; color: #333;">
                      ${defect.isValidatedDefect ? '🔴 Validated Defect' : '🟡 Pending Finding'}
                    </h3>
                    <p style="margin: 5px 0;"><strong>ID:</strong> ${defect.isValidatedDefect ? `D-${defect.defectId}` : `F-${defect.id}`}</p>
                    <p style="margin: 5px 0;"><strong>Type:</strong> ${defect.defectType}</p>
                    <p style="margin: 5px 0;"><strong>Status:</strong> ${defect.validationStatus}</p>
                    <p style="margin: 5px 0;"><strong>Confidence:</strong> ${(defect.confidence * 100).toFixed(1)}%</p>
                    <p style="margin: 5px 0;"><strong>Device:</strong> ${defect.deviceName}${defect.vehicleId ? ` (${defect.vehicleId})` : ''}</p>
                    <p style="margin: 5px 0;"><strong>Location:</strong> ${[defect.networkName, defect.sectionName, defect.chainageName].filter(Boolean).join(' • ') || 'Out of Boundary'}</p>
                    <p style="margin: 5px 0;"><strong>Coordinates:</strong> ${defect.latitude.toFixed(6)}, ${defect.longitude.toFixed(6)}</p>
                    <p style="margin: 5px 0;"><strong>Detected:</strong> ${new Date(defect.detectedAt).toLocaleString()}</p>
                    ${defect.priority ? `<p style="margin: 5px 0;"><strong>Priority:</strong> ${defect.priority}</p>` : ''}
                    <hr style="margin: 10px 0; border: none; border-top: 1px solid #ddd;">
                    <p style="margin: 5px 0; font-style: italic; color: #666; text-align: center;">Click to view details</p>
                  </div>
                `}
                position={Cartesian3.fromDegrees(defect.longitude, defect.latitude, 0)}
              >
                <PointGraphics
                  pixelSize={defect.isValidatedDefect ? 12 : 10}
                  color={Color.fromCssColorString(
                    defect.isValidatedDefect 
                      ? '#FF4444' // Red for validated defects
                      : defect.validationStatus === 'rejected' 
                        ? '#888888' // Gray for rejected findings
                        : '#FFA500' // Orange for pending findings
                  )}
                  outlineColor={Color.WHITE}
                  outlineWidth={2}
                  heightReference={Cesium.HeightReference.CLAMP_TO_GROUND}
                  disableDepthTestDistance={Number.POSITIVE_INFINITY}
                  scaleByDistance={new Cesium.NearFarScalar(500, 1.0, 15000, 0.7)}
                  translucencyByDistance={new Cesium.NearFarScalar(500, 1.0, 15000, 0.8)}
                />
              </Entity>
            ))}
          </>
        )}

        {/* Render asset footprints when enabled - only in Asset mode */}
        {selectedTab === 0 && isFootprintVisible && selectedFootprintAsset && (
          <AssetFootprintLayer 
            assets={[selectedFootprintAsset]} 
          />
        )}

        {/* Render LayerControl to handle GeoJSON, IoT, Tilesets */}
        {/* LayerControl will get viewerRef from context */}
        <LayerControl
          showRoadLines={showRoadLines}
          showIoTSensors={showIoTSensors}
          show3DModel={show3DModel}
          selectedTilesetAsset={selectedTilesetAsset}
          handleSensorSelect={handleSensorSelect}
          handleSensorUpdate={handleSensorUpdate}
          onTilesetLoadError={handleTilesetLoadError} // Add error handler
          organizationLayers={organizationLayers} // Pass dynamic organization layers
          loadingOrganizationLayers={loadingOrganizationLayers} // Pass loading state
          onTilesetReady={handleTilesetReady}
          selected3dModelVersion={selected3dModelVersion}
        />

        {/* Render InteractionHandler (doesn't render UI, just sets up effects) */}
        {/* InteractionHandler will get viewerRef from context */}
        <InteractionHandler
          assets={assets}
          setTooltipVisible={setTooltipVisible}
          setTooltipContent={setTooltipContent}
          setTooltipPosition={setTooltipPosition}
          setSelectedAsset={setSelectedAsset}
          setIsDrawerOpen={setIsDrawerOpen}
          // Pass pothole setters now that logic is consolidated
          setSelectedPothole={setSelectedPothole}
          setIsPotholeModalOpen={setIsPotholeModalOpen}
          is3DModelVisible={show3DModel} // Pass 3D model visibility state
          // Pass handlers and state for closing panels on new selection
          handleClose3DModel={handleClose3DModel}
          handleCloseInspectionPanel={handleCloseInspectionPanel}
          show3DModel={show3DModel}
          showInspectionPanel={showInspectionPanel}
          // Pass current asset IDs for comparison
          current3DModelAssetId={selectedTilesetAsset?.id}
          currentInspectionAssetId={inspectionAsset?.id}
          // Pass footprint handlers and state
          isFootprintVisible={isFootprintVisible}
          setIsFootprintVisible={setIsFootprintVisible}
          setSelectedFootprintAsset={setSelectedFootprintAsset}
          currentFootprintAssetId={selectedFootprintAsset?.id || selectedFootprintAsset?.assetId}
        />

        {/* Render AnimationManager only after initial view is set */}
        {isInitialViewSettled && (
          <AnimationManager
            onTruckEntityCreated={setTrackedTruckEntityRef} // Pass callback to get truck ref
            shouldLoadAnimations={shouldLoadAnimations} // Pass animation trigger state
            onWaypointNotify={handleWaypointNotification} // Pass notification handler
          />
        )}

      </Viewer>

      {/* Floating Tab Button - positioned slightly under navbar at center */}
      <Box
        sx={{
          position: 'absolute',
          top: 20, // Much closer to the navbar
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000, // High z-index to ensure it's above other elements
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: '16px',
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            overflow: 'hidden',
            padding: '2px',
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': {
                display: 'none', // Remove the indicator for cleaner look
              },
              '& .MuiTab-root': {
                minWidth: 90,
                minHeight: 36,
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '0.02em',
                color: theme.palette.text.secondary,
                transition: 'all 0.15s ease',
                borderRadius: '12px',
                margin: '2px',
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '&.Mui-selected': {
                  color: 'white',
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
                '&.Mui-disabled': {
                  color: theme.palette.text.disabled,
                  opacity: 0.5,
                  cursor: 'not-allowed',
                },
              },
            }}
          >
            <Tab label="Asset" />
            <Tab
              label="Operation"
              disabled={!hasRoadPavementDiscipline}
              title={!hasRoadPavementDiscipline ? "Requires Road Pavement discipline" : ""}
            />
          </Tabs>
        </Paper>
      </Box>

      {/* Render the custom BasemapPicker */}
      <Slide direction="left" in={isButtonVisible('basemap')} mountOnEnter unmountOnExit>
        <Box>
          <BasemapPicker
            basemaps={basemapOptions}
            onSelectBasemap={handleBasemapChange}
          />
        </Box>
      </Slide>
      {/* Render the TerrainPicker */}
      <Slide direction="left" in={isButtonVisible('terrain')} mountOnEnter unmountOnExit>
        <Box>
          <TerrainPicker
            terrainProviders={mapConfig.terrain.providers}
            currentProvider={currentTerrainProvider}
            terrainEnabled={terrainEnabled}
            terrainLoading={terrainLoading}
            onProviderChange={handleTerrainProviderChange}
            onTerrainToggle={handleTerrainToggle}
          />
        </Box>
      </Slide>
      {/* Home Button */}
      <Slide direction="left" in={isButtonVisible('home')} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            top: 16, // Same top position as BasemapPicker
            right: 16, // Same right position as BasemapPicker
            zIndex: 950, // Same z-index as other controls
            mt: '60px', // Increased margin-top to create more space between buttons
          }}
        >
          <Tooltip title="Home" placement="left">
            <IconButton
              onClick={handleHomeClick}
              size="large"
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.85),
                color: 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
              }}
            >
              <HomeIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Slide>
      {/* Render FilterManager */}
      <Slide direction="right" in={isButtonVisible('filter')} mountOnEnter unmountOnExit>
        <Box>
          <FilterManager
            onFilterChange={handleFilterChange}
            assets={assets}
            displayedCount={assets.length}
            showRoadLines={showRoadLines}
            setShowRoadLines={setShowRoadLines}
            showIoTSensors={showIoTSensors}
            setShowIoTSensors={setShowIoTSensors}
            showAssetFootprints={showAssetFootprints}
            setShowAssetFootprints={setShowAssetFootprints}
            closeSignal={filterCloseSignal}
          />
        </Box>
      </Slide>

      {/* Render OperationFilterManager - Only in Operation Mode */}
      <Slide direction="right" in={isButtonVisible('operationFilter')} mountOnEnter unmountOnExit>
        <Box>
          <OperationFilterManager
            onFilterChange={handleOperationFilterManagerChange}
            operationDefects={filteredOperationDefects}
            displayedCount={filteredOperationDefects.length}
            closeSignal={filterCloseSignal}
          />
        </Box>
      </Slide>

      {/* Tracking and Batch Upload Buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: 16,
          transform: 'translateY(-50%)',
          zIndex: 950,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Slide direction="left" in={isButtonVisible('batchUpload')} mountOnEnter unmountOnExit>
          <Tooltip 
            title="Batch Upload" 
            placement="left"
          >
            <IconButton
              onClick={handleBatchUploadOpen}
              size="large"
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.85),
                color: 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Tooltip>
        </Slide>
        <Slide direction="left" in={isButtonVisible('tracking')} mountOnEnter unmountOnExit>
          <Tooltip 
            title={isTrackingEnabled ? "Disable Tracking" : "Enable Tracking"} 
            placement="left"
          >
            <IconButton
              onClick={toggleTracking}
              size="large"
              sx={{
                backgroundColor: isTrackingEnabled 
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
                color: isTrackingEnabled ? 'white' : 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isTrackingEnabled 
                    ? alpha(theme.palette.primary.main, 1)
                    : alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
              }}
            >
              <TrackChangesIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Tooltip>
        </Slide>
      </Box>
      
      {/* Tooltip Element */}
      {tooltipVisible && (
        <Box
          sx={{
            position: 'absolute',
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            color: theme.palette.text.primary,
            padding: '10px 14px',
            borderRadius: '12px',
            fontSize: '0.85rem',
            fontWeight: 500,
            letterSpacing: '0.01em',
            lineHeight: 1.5,
            pointerEvents: 'none', // Prevent tooltip from blocking mouse events
            zIndex: 1100,
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 4px 10px -5px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            maxWidth: '280px',
            transform: 'translate(-10px, -10px)',
            transition: 'opacity 0.15s ease, transform 0.15s ease',
          }}
          dangerouslySetInnerHTML={{ __html: tooltipContent }}
        />
      )}
      {/* Render the Asset Detail Drawer */}
      <AssetDetailDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        asset={selectedAsset}
        onShowInspection={handleShowInspection}
        onShow3DModel={handleShow3DModel}
        // Pass 3D model props
        is3DModelVisible={show3DModel}
        activeTilesetAsset={selectedTilesetAsset}
        // Pass inspection props
        isInspectionVisible={showInspectionPanel}
        inspectionAsset={inspectionAsset}
        // Pass footprint props
        isFootprintVisible={isFootprintVisible}
        onToggleFootprint={handleToggleFootprint}
        activeFootprintAsset={selectedFootprintAsset}
        onVersionsLoaded={setAvailable3dModelVersions}
        onAssetUpdate={handleAssetUpdate}
      />
      {/* Render the IoT Sensor Detail Drawer with updated sensor data */}
      <IoTDetailDrawer
        open={isIoTDrawerOpen}
        onClose={handleCloseIoTDrawer}
        sensor={selectedSensor}
      />
      {/* Render the Detection Detail Modal for potholes */}
      <DetectionDetailModal
        open={isPotholeModalOpen}
        onClose={handleClosePotholeModal}
        detection={selectedPothole}
      />
      {/* FilterPanel is now rendered inside FilterManager */}

      {/* Camera Height Display Overlay (hidden via SHOW_STATUS_OVERLAY) */}
      {SHOW_STATUS_OVERLAY && (
      <Box
        sx={{
          position: 'absolute',
          bottom: 0, // Changed from 60 to 0 to position at bottom edge
          left: 0,  // Changed from 10 to 0 to position at left edge
          zIndex: 900, // Ensure it's above the map but below the inspection panel
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '3px 8px',
          borderRadius: '4px 4px 0 0', // Rounded only top corners since it's at bottom edge
          fontSize: '0.8rem',
          fontFamily: 'monospace', // Use monospace for number alignment
          pointerEvents: 'none', // Don't interfere with map interaction
        }}
      >
        {/* User info */}
        {isAuthenticated && user && (
          <>
            <span>
              User: {user.fullName || user.email || 'Unknown'} ({userRole})
            </span>
            <span style={{ margin: '0 5px' }}>|</span>
            <span>
              Org: {userOrganization?.name || 'N/A'}
            </span>
            <span style={{ margin: '0 5px' }}>|</span>
          </>
        )}
        
        {/* Camera altitude */}
        {cameraHeight !== null ? `Alt: ${Math.round(cameraHeight)} m` : 'Altitude: N/A'}
        
        {/* Terrain status */}
        <span style={{ margin: '0 5px' }}>|</span>
        <span>
          Terrain: {terrainEnabled ? (
            currentTerrainProvider?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Active'
          ) : 'Disabled'}
        </span>
        
        {/* Loading indicator */}
        {terrainLoading && (
          <>
            <span style={{ margin: '0 5px' }}>|</span>
            <span>Loading...</span>
          </>
        )}
        
        {/* Selected Asset Name - Display if an asset is selected (Asset Mode only) */}
        {selectedTab === 0 && selectedAsset && (
          <>
            {/* Separator */}
            <span style={{ margin: '0 5px' }}>|</span> 
            {/* Asset Name or Code */}
            <span>
              Sel: {selectedAsset.name || selectedAsset.code || 'Selected Asset'}
            </span>
          </>
        )}
        
        {/* Operation Mode Info - Display when in Operation Mode */}
        {selectedTab === 1 && (
          <>
            {/* Separator */}
            <span style={{ margin: '0 5px' }}>|</span> 
            {/* Operation Mode Status */}
            <span>
              Operation: {loadingOperationDefects ? 'Loading...' : `${filteredOperationDefects.length} defects (${new Date().toLocaleDateString()})`}
            </span>
          </>
        )}
      </Box>
      )}

      {/* Inspection Panel - Only show when not in fullscreen mode */}
      {!inspectionFullScreenMode && (
        <InspectionPanel
          open={showInspectionPanel}
          onClose={handleCloseInspectionPanel}
          asset={inspectionAsset}
          onSelectInspection={handleSelectInspection}
          onAddInspection={handleAddInspection}
          onEditInspection={handleEditInspection}
          onPlotImages={handlePlotInspectionImages}
          onRemoveImages={handleRemoveInspectionImages}
          refreshKey={inspectionRefreshKey}
          onEnterFullScreenMode={handleEnterInspectionFullScreen}
          isFullScreenMode={false}
        />
      )}

      {/* Render the add inspection modal if visible */}
      <AddInspectionModal
        open={showAddInspectionModal}
        onClose={handleCloseAddInspectionModal}
        asset={inspectionAsset}
        onSuccess={handleInspectionCreated}
      />

      {/* Render the edit inspection modal if visible */}
      <EditInspectionModal
        open={showEditInspectionModal}
        onClose={handleCloseEditInspectionModal}
        inspection={editInspection}
        onSuccess={handleInspectionUpdated}
      />

      {/* Render the inspection details modal if visible - Only show when not in fullscreen mode */}
      {!inspectionFullScreenMode && (
        <InspectionDetailsModal
          open={showInspectionGallery}
          onClose={handleCloseInspectionGallery}
          inspection={selectedInspection}
          onPhotoSelect={handlePhotoSelect} // Pass the handler function
          onDataMutated={triggerInspectionPanelRefresh} // Pass the refresh trigger callback
          fullScreenMode={false}
        />
      )}

      {/* Batch Upload Modal */}
      <BatchUploadModal 
        open={batchUploadModalOpen} 
        onClose={handleBatchUploadClose}
      />

      {/* MODIFIED Quick Access Drawer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 950, // Ensure it's above camera height, but can be below other panels if needed
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Drawer Content - slides up when open */}
        <Slide direction="up" in={quickAccessOpen} mountOnEnter unmountOnExit>
          <Paper
            elevation={6}
            sx={{
              width: 300, // Slightly reduced width
              borderRadius: '24px 24px 0 0',
              background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
              backdropFilter: 'blur(12px)',
              padding: '12px 8px 8px',
              paddingBottom: '28px', 
              mb: '-16px', 
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              borderBottom: 'none',
              overflow: 'hidden', // Ensure nothing breaks out of the container
            }}
          >
            <Typography 
              variant="subtitle2" 
              display="block" 
              textAlign="center" 
              sx={{ 
                mb: 1.5, 
                fontWeight: 600,
                letterSpacing: '0.01em',
                color: theme.palette.text.primary,
                fontSize: '0.75rem'
              }}
            >
              Quick Filter by Region (PLUS Malaysia)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%',
                mt: 0.5,
              }}
            >
              {quickAccessRegions.map((region) => (
                <Tooltip title={`Filter by ${region.name} Region`} placement="top" arrow key={region.value}>
                  <Box 
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .region-icon': {
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          transform: 'scale(1.1)',
                          boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                        },
                        '& .region-name': {
                          color: theme.palette.primary.main,
                          fontWeight: 600
                        }
                      },
                      userSelect: 'none',
                    }}
                    onClick={() => handleQuickRegionSelect(region.value)}
                  >
                    <Box 
                      className="region-icon"
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 36,
                        height: 36,
                        bgcolor: alpha(theme.palette.action.hover, 0.2),
                        color: theme.palette.text.secondary,
                        borderRadius: '50%',
                        mb: 0.5,
                        transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }}
                    >
                      {region.icon}
                    </Box>
                    <Typography 
                      className="region-name"
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.65rem', 
                        fontWeight: 500, 
                        color: theme.palette.text.secondary,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {region.name}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        </Slide>
        
        {/* Drawer Handle - simplified hover effect */}
                  <Paper
            elevation={4}
            onClick={toggleQuickAccess}
            sx={{
              width: 45, 
              height: 18, 
              borderRadius: '12px 12px 0 0',
              background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.85)}, ${alpha(theme.palette.background.paper, 0.95)})`,
              backdropFilter: 'blur(10px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                transform: 'translateY(-3px)',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
              },
              zIndex: 960, // Above the content panel when closed
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              borderBottom: 'none',
              boxShadow: '0 -2px 6px rgba(0,0,0,0.08)'
                          }}
            >
            <KeyboardArrowUpIcon
            sx={{
              transform: quickAccessOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              fontSize: '0.95rem', 
              color: quickAccessOpen ? theme.palette.primary.main : theme.palette.text.secondary,
              opacity: 0.9,
            }}
          />
        </Paper>
      </Box>

      {/* Legend Button and Panel */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 16,
          zIndex: 950,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 2,
        }}
      >
        {/* Legend Panel */}
        {legendOpen && (
          <Paper
            elevation={6}
            sx={{
              p: 2.5,
              borderRadius: '16px',
              backgroundColor: alpha(theme.palette.background.paper, 0.85),
              backdropFilter: 'blur(10px)',
              mb: 1.5,
              maxWidth: 240,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(10px) scale(0.95)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0) scale(1)',
                },
              },
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 1.5, 
                fontWeight: 600,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                pb: 1,
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.9rem',
                userSelect: 'none', // Prevent text selection
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 0.8, color: theme.palette.primary.main }} />
              {selectedTab === 0
                ? (currentFilters.category_id === '3' || currentFilters.category_id === 3 ||
                   currentFilters.type_id === '4' || currentFilters.type_id === 4)
                  ? 'Hazard & Risk Rating'
                  : 'Asset Categories'
                : 'Defects & Findings'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, userSelect: 'none' }}> {/* Prevent text selection */}
              {selectedTab === 0 ? (
                // Asset Mode Legend - show rating colors when Geotechnical/Slope selected
                (currentFilters.category_id === '3' || currentFilters.category_id === 3 ||
                 currentFilters.type_id === '4' || currentFilters.type_id === 4) ? (
                  // Rating colors legend
                  [
                    { name: 'Rx1', color: '#2afebe' },
                    { name: 'Rx2', color: '#9eff3d' },
                    { name: 'Rx3', color: '#feb83e' },
                    { name: 'Rx4', color: '#fd5353' },
                    { name: 'Rx5', color: '#a80000' },
                    { name: 'Not Rated', color: '#9e9e9e' },
                  ].map(({ name, color }) => (
                    <Box
                      key={name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.8,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: color,
                          mr: 1.5,
                          boxShadow: `0 0 0 3px ${alpha(color, 0.2)}`
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          letterSpacing: '0.01em',
                          color: theme.palette.text.primary
                        }}
                      >
                        {name}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  // Category colors legend (default)
                  Object.entries(categoryColors).map(([category, color]) => (
                    <Box
                      key={category}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.8,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: color,
                          mr: 1.5,
                          boxShadow: `0 0 0 3px ${alpha(color, 0.2)}`
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          letterSpacing: '0.01em',
                          color: theme.palette.text.primary
                        }}
                      >
                        {category}
                      </Typography>
                    </Box>
                  ))
                )
              ) : (
                // Operation Mode Legend
                [
                  { label: 'Validated Defects', color: '#FF4444', description: 'Confirmed defects' },
                  { label: 'Pending Findings', color: '#FFA500', description: 'Awaiting validation' },
                  { label: 'Rejected Findings', color: '#888888', description: 'Validation rejected' }
                ].map((item) => (
                  <Box 
                    key={item.label} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 0.8,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      userSelect: 'none', // Prevent text selection
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: item.color, 
                        mr: 1.5,
                        boxShadow: `0 0 0 3px ${alpha(item.color, 0.2)}`
                      }} 
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography 
                        variant="body2"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          letterSpacing: '0.01em',
                          color: theme.palette.text.primary,
                          lineHeight: 1.2
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography 
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          lineHeight: 1.1
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        )}
        
        {/* Legend Button */}
        <Slide direction="left" in={isButtonVisible('legend')} mountOnEnter unmountOnExit>
          <Tooltip title="Asset Categories Legend" placement="left">
            <IconButton
              onClick={toggleLegend}
              size="medium"
              sx={{
                backgroundColor: legendOpen 
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
                color: legendOpen ? 'white' : 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: legendOpen 
                    ? alpha(theme.palette.primary.main, 1)
                    : alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
                width: 40,
                height: 40,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Tooltip>
        </Slide>
      </Box>
    </Box>

      {/* Enhanced Snackbar for Waypoint Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000} // Increased duration for better readability
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Changed position to top-center
        TransitionComponent={Zoom} // Added zoom transition effect
        sx={{
          marginTop: 2, // Add some margin from the top
        }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            minWidth: '300px', // Minimum width for better visibility
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 8px 16px rgba(0,0,0,0.15)', // Enhanced shadow
            padding: '10px 16px', // More comfortable padding
            '.MuiAlert-icon': {
              fontSize: '1.4rem', // Larger icon
              marginRight: '12px', // More space between icon and text
            },
            '.MuiAlert-message': {
              fontSize: '0.95rem', // Slightly larger text
              display: 'flex',
              alignItems: 'center',
              fontFamily: (theme) => theme.typography.fontFamily, // Ensure consistent font
            },
            '.MuiAlert-action': {
              paddingTop: '4px', // Align close button better
            },
            border: (theme) => `1px solid ${theme.palette[snackbarSeverity]?.light || theme.palette.info.light}`, // Subtle border
            backdropFilter: 'blur(8px)', // Modern blur effect
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Render the Photo Viewer Modal - Only show when NOT in fullscreen mode */}
      {/* In fullscreen mode, the photo viewer is rendered inline in the main content area */}
      {!inspectionFullScreenMode && (
        <Enhanced_ImageViewerModal
          open={showPhotoViewer}
          onClose={handleClosePhotoModal} // Use the consolidated close handler
          photo={selectedPhoto}
          onDataMutated={triggerInspectionPanelRefresh}
          viewOnlyMode={isPhotoViewOnly} // Use the new state for viewOnlyMode
          // New navigation props
          photos={photoArray}
          currentIndex={currentPhotoIndex}
          onNavigate={handlePhotoNavigation}
          fullScreenMode={false}
        />
      )}

      {/* DefectValidationModal for Operation Mode - View Only */}
      {selectedDefectForValidation && (
        <DefectValidationModal
          open={defectValidationModalOpen}
          onClose={handleDefectModalClose}
          selectedDefect={selectedDefectForValidation}
          defectTypesList={defectTypesList}
          currentDefectTypeId={selectedDefectForValidation.rawData?.defect_type_id || selectedDefectForValidation.rawData?.validated_defect_type_id}
          onDefectTypeChange={() => {}} // No-op for view-only
          rejectionReason=""
          onRejectionReasonChange={() => {}} // No-op for view-only
          loadingDefectTypes={loadingDefectTypes}
          onValidate={() => {}} // No-op for view-only
          onReject={() => {}} // No-op for view-only
          formatDateTime={(date) => {
            if (!date) return 'N/A';
            return new Date(date).toLocaleString();
          }}
          viewOnly={true} // This makes the modal read-only
        />
      )}

      {/* Render the TimelineSlider when a 3D model is active */}
      {show3DModel && (
        <TimelineSlider
          versions={available3dModelVersions.available}
          selectedVersion={selected3dModelVersion}
          onVersionChange={setSelected3dModelVersion}
        />
      )}
    </>
  );
};


export default MapViewPage;